export const PYTHON_INTERVIEW_PROBLEMS = [
    {
      id: 1,
      title: "Classic FizzBuzz Challenge",
      slug: "python-classic-fizzbuzz-challenge",
      seoTitle: "Python FizzBuzz Interview Problem | Repractiq",
      metaDescription: "Solve the classic FizzBuzz interview question in Python evaluating modulo tracking and string logic.",
      tags: ["conditionals", "modulo", "strings"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `fizzbuzz(n)` that returns 'Fizz' if `n` is divisible by 3, 'Buzz' if divisible by 5, 'FizzBuzz' if divisible by both 3 and 5, and the number as a string if none apply.",
      explanation: "Evaluate the combined condition first (divisible by 15) to prevent early termination by standalone checks.",
      hint: "Check `n % 15 == 0` first.",
      starterCode: `def fizzbuzz(n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "15", call: "fizzbuzz(15)", expected: "FizzBuzz" },
        { input: "9", call: "fizzbuzz(9)", expected: "Fizz" },
        { input: "4", call: "fizzbuzz(4)", expected: "4" }
      ],
    },
    {
      id: 2,
      title: "N-th Fibonacci Number",
      slug: "python-nth-fibonacci-number",
      seoTitle: "Python N-th Fibonacci Loop | Repractiq",
      metaDescription: "Find the n-th Fibonacci number efficiently using an iterative calculation strategy.",
      tags: ["loops", "math"],
      difficulty: "Easy",
      type: "function",
      description: "Write an iterative function `fibonacci(n)` that returns the n-th Fibonacci number (where F(0)=0, F(1)=1).",
      explanation: "Iterative approaches achieve O(N) time and O(1) space, avoiding execution stacks of unmemoized recursion.",
      hint: "Use tuple unpacking to update values: a, b = b, a + b.",
      starterCode: `def fibonacci(n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "0", call: "fibonacci(0)", expected: "0" },
        { input: "6", call: "fibonacci(6)", expected: "8" }
      ],
    },
    {
      id: 3,
      title: "Valid Alphanumeric Palindrome",
      slug: "python-valid-alphanumeric-palindrome",
      seoTitle: "Python Palindrome String Checking | Repractiq",
      metaDescription: "Check if a string is a palindrome after sanitizing non-alphanumeric values and casing.",
      tags: ["strings", "slicing"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `is_palindrome(s)` that determines if a string is a palindrome, ignoring non-alphanumeric characters and letter case.",
      explanation: "Sanitize the raw string via character validation or filter steps, then confirm structural symmetry.",
      hint: "Use `c.isalnum()` to filter out special characters.",
      starterCode: `def is_palindrome(s):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'A man, a plan, a canal: Panama'", call: "is_palindrome('A man, a plan, a canal: Panama')", expected: "True" },
        { input: "'race a car'", call: "is_palindrome('race a car')", expected: "False" }
      ],
    },
    {
      id: 4,
      title: "Valid Anagram Counter Check",
      slug: "python-valid-anagram-counter-check",
      seoTitle: "Python Valid Anagram Hash Map | Repractiq",
      metaDescription: "Determine if two strings are anagrams of each other using frequency hashes or sorting.",
      tags: ["strings", "dictionaries"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `is_anagram(s, t)` that returns True if string `t` is an anagram of `s`, and False otherwise.",
      explanation: "Two strings are anagrams if their sorted permutations are matching, or if total character counts match.",
      hint: "Python's `collections.Counter` directly builds frequency map comparisons.",
      starterCode: `from collections import Counter\n\ndef is_anagram(s, t):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'anagram', 'nagaram'", call: "is_anagram('anagram', 'nagaram')", expected: "True" },
        { input: "'rat', 'car'", call: "is_anagram('rat', 'car')", expected: "False" }
      ],
    },
    {
      id: 5,
      title: "Two Sum Hash Map Target",
      slug: "python-two-sum-hash-map-target",
      seoTitle: "Python Two Sum Index Resolution | Repractiq",
      metaDescription: "Solve the classic Two Sum interview problem using a single-pass hash map approach for optimal runtime.",
      tags: ["arrays", "dictionaries"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `two_sum(nums, target)` that returns the indices of the two elements that sum up to `target`.",
      explanation: "Store visited numbers and their matching index inside a hash map to process lookups in O(1) average time.",
      hint: "Compute the difference `target - num` and look for it in your index cache.",
      starterCode: `def two_sum(nums, target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[2, 7, 11, 15], 9", call: "two_sum([2, 7, 11, 15], 9)", expected: "[0, 1]" },
        { input: "[3, 2, 4], 6", call: "two_sum([3, 2, 4], 6)", expected: "[1, 2]" }
      ],
    },
    {
      id: 6,
      title: "Linked List Node Reversal Concept",
      slug: "python-linked-list-node-reversal-concept",
      seoTitle: "Python Reverse Linked List Simulated | Repractiq",
      metaDescription: "Simulate pointer manipulation workflows to reverse sequences using node state structures.",
      tags: ["linked lists", "advanced oop"],
      difficulty: "Medium",
      type: "function",
      description: "Implement a function `reverse_list_simulation(node_values)` that acts like a pointer re-linker to output reversed list sequences without standard slicing.",
      explanation: "Reversal relies on three changing tracking points: current, previous, and next storage pointers.",
      hint: "Iterate through the array representing nodes, tracking links as values change directions.",
      starterCode: `def reverse_list_simulation(node_values):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 3, 4]", call: "reverse_list_simulation([1, 2, 3, 4])", expected: "[4, 3, 2, 1]" }
      ],
    },
    {
      id: 7,
      title: "Binary Search Array Boundaries",
      slug: "python-binary-search-array-boundaries",
      seoTitle: "Python Sorted Binary Search Index | Repractiq",
      metaDescription: "Find values in a sorted collection inside logarithmic time using explicit binary space reduction.",
      tags: ["algorithms", "performance optimization"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `binary_search(nums, target)` that looks for `target` within a sorted list `nums`, returning its zero-indexed index or `-1`.",
      explanation: "Continually halve search horizons using two pointers to achieve optimal O(log N) time guarantees.",
      hint: "Calculate mid index dynamically: `mid = (left + right) // 2`.",
      starterCode: `def binary_search(nums, target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[-1, 0, 3, 5, 9, 12], 9", call: "binary_search([-1, 0, 3, 5, 9, 12], 9)", expected: "4" },
        { input: "[-1, 0, 3, 5, 9, 12], 2", call: "binary_search([-1, 0, 3, 5, 9, 12], 2)", expected: "-1" }
      ],
    },
    {
      id: 8,
      title: "Merge Two Sorted Lists",
      slug: "python-merge-two-sorted-lists",
      seoTitle: "Python Merge Sorted Arrays Two Pointers | Repractiq",
      metaDescription: "Combine two sorted array structures into a single continuous sorted sequence using two pointers.",
      tags: ["arrays", "sorting"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `merge_sorted_lists(list1, list2)` that combines two pre-sorted lists into a single continuous sorted list.",
      explanation: "Maintain two pointers tracking heads across sequences, appending smaller numbers step-by-step.",
      hint: "Remember to append left-over values when one loop terminates early.",
      starterCode: `def merge_sorted_lists(list1, list2):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 4], [1, 3, 4]", call: "merge_sorted_lists([1, 2, 4], [1, 3, 4])", expected: "[1, 1, 2, 3, 4, 4]" }
      ],
    },
    {
      id: 9,
      title: "Valid Parentheses Balance Check",
      slug: "python-valid-parentheses-balance-check",
      seoTitle: "Python Bracket Balancing Stack | Repractiq",
      metaDescription: "Determine structural bracket closure validity using a Last-In-First-Out (LIFO) stack collection structure.",
      tags: ["stacks", "strings"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `is_valid_parentheses(s)` that verifies string grouping brackets context sequences return closed balances accurately.",
      explanation: "Push opening brackets onto a stack, popping to compare compatibility when matching closing brackets appear.",
      hint: "A lookup dictionary mapping closing brackets to opening brackets makes checking clean.",
      starterCode: `def is_valid_parentheses(s):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'()[]{}'", call: "is_valid_parentheses('()[]{}')", expected: "True" },
        { input: "'(]'", call: "is_valid_parentheses('(]')", expected: "False" }
      ],
    },
    {
      id: 10,
      title: "Find Duplicate Items Array",
      slug: "python-find-duplicate-items-array",
      seoTitle: "Python Duplicate Detection Set Mapping | Repractiq",
      metaDescription: "Locate items appearing multiple times inside collection inputs using specialized storage sets.",
      tags: ["arrays", "sets"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `find_duplicates(nums)` that isolates and returns all values occurring more than once inside an array.",
      explanation: "Track visited elements in a set to find duplicates in linear time.",
      hint: "Initialize a seen set and a results set to collect matching values without repeating answers.",
      starterCode: `def find_duplicates(nums):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[4, 3, 2, 7, 8, 2, 3, 1]", call: "sorted(find_duplicates([4, 3, 2, 7, 8, 2, 3, 1]))", expected: "[2, 3]" }
      ],
    },
    {
      id: 11,
      title: "FizzBuzz String Accumulator Array",
      slug: "python-fizzbuzz-string-accumulator-array",
      seoTitle: "Python FizzBuzz Output Collector | Repractiq",
      metaDescription: "Generate historical lists containing full sequences of FizzBuzz string structures up to boundary targets.",
      tags: ["arrays", "loops"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `generate_fizzbuzz_list(limit)` that builds an array tracking the sequence output strings up to a positive target.",
      explanation: "Iterate across numerical increments, generating custom strings depending on composite conditional logic rules.",
      hint: "Use string formatting loops or list comprehensions calling an underlying mapping method.",
      starterCode: `def generate_fizzbuzz_list(limit):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "5", call: "generate_fizzbuzz_list(5)", expected: "['1', '2', 'Fizz', '4', 'Buzz']" }
      ],
    },
    {
      id: 12,
      title: "Fibonacci Sequence Array List Generator",
      slug: "python-fibonacci-sequence-array-list-generator",
      seoTitle: "Python Complete Fibonacci Sequence Array | Repractiq",
      metaDescription: "Generate a list containing all sequence values up to a given sequence position.",
      tags: ["arrays", "math"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `generate_fibonacci_sequence(n)` that returns a list containing all Fibonacci numbers from index `0` up to length position `n`.",
      explanation: "Populate the array step-by-step using structural indices, dynamically fetching previous entries.",
      hint: "Seed arrays directly using `[0, 1]` for scenarios containing lengths greater than 1.",
      starterCode: `def generate_fibonacci_sequence(n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "5", call: "generate_fibonacci_sequence(5)", expected: "[0, 1, 1, 2, 3, 5]" }
      ],
    },
    {
      id: 13,
      title: "Longest Palindromic Substring Window",
      slug: "python-longest-palindromic-substring-window",
      seoTitle: "Python Longest Palindrome Center Expansion | Repractiq",
      metaDescription: "Locate long structural palindromes inside asymmetrical strings using optimal center expansion checks.",
      tags: ["strings", "algorithms"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `longest_palindrome(s)` that identifies and extracts the longest substring partition layout containing mirrored properties.",
      explanation: "Expand outward from each character index to evaluate odd and even palindrome structural patterns.",
      hint: "Consider both single-character centers ('aba') and double-character centers ('bbat').",
      starterCode: `def longest_palindrome(s):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'babad'", call: "longest_palindrome('babad')", expected: "'bab'" },
        { input: "'cbbd'", call: "longest_palindrome('cbbd')", expected: "'bb'" }
      ],
    },
    {
      id: 14,
      title: "Group Anagram Hash Collections",
      slug: "python-group-anagram-hash-collections",
      seoTitle: "Python Group Anagrams Dictionaries | Repractiq",
      metaDescription: "Sort lists of strings into clustered grouping arrays where each collection consists of shared letter anagrams.",
      tags: ["strings", "dictionaries"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `group_anagrams(strs)` that accepts an array of strings and clusters matching letter configurations into sublists.",
      explanation: "Use sorted strings as unique tracking keys inside a dictionary, appending source variations to list buckets.",
      hint: "Convert `sorted(word)` into a tuple to use it as a valid dictionary key.",
      starterCode: `from collections import defaultdict\n\ndef group_anagrams(strs):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "['eat', 'tea', 'tan', 'ate', 'nat', 'bat']", call: "[sorted(x) for x in group_anagrams(['eat', 'tea', 'tan', 'ate', 'nat', 'bat'])]", expected: "[['ate', 'eat', 'tea'], ['bat'], ['nat', 'tan']]" }
      ],
    },
    {
      id: 15,
      title: "Two Sum Sorted Array Inputs",
      slug: "python-two-sum-sorted-array-inputs",
      seoTitle: "Python Two Sum Two Pointers Sorted | Repractiq",
      metaDescription: "Optimize target indices scanning matching sorted configurations using spatial narrowing pointers.",
      tags: ["arrays", "sorting"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `two_sum_sorted(numbers, target)` that finds target matching configurations when inputs arrive pre-sorted.",
      explanation: "Place tracking pointers at opposite ends of the collection, narrowing indices depending on current evaluations.",
      hint: "Increment the left pointer if the sum is too low, and decrement the right pointer if the sum is too high.",
      starterCode: `def two_sum_sorted(numbers, target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[2, 7, 11, 15], 9", call: "two_sum_sorted([2, 7, 11, 15], 9)", expected: "[1, 2]" }
      ],
    },
    {
      id: 16,
      title: "Reverse Linked List Window Segment",
      slug: "python-reverse-linked-list-window-segment",
      seoTitle: "Python In-Place Linked List Segment Reversal | Repractiq",
      metaDescription: "Reverse a specific linked section within target lists based on boundary conditions.",
      tags: ["linked lists", "algorithms"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `reverse_segment(nodes, left, right)` that reverses elements between indices `left` and `right`.",
      explanation: "Isolate structural segment paths cleanly before switching node relationships to avoid breaks.",
      hint: "Use dummy heads to simplify pointer switching tracking logic near front edges.",
      starterCode: `def reverse_segment(nodes, left, right):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 3, 4, 5], 2, 4", call: "reverse_segment([1, 2, 3, 4, 5], 2, 4)", expected: "[1, 4, 3, 2, 5]" }
      ],
    },
    {
      id: 17,
      title: "Search Insert Order Position",
      slug: "python-search-insert-order-position",
      seoTitle: "Python Bisect Insert Position Index | Repractiq",
      metaDescription: "Find target matching locations or identify the exact insertion index for missing values in sorted arrays.",
      tags: ["arrays", "algorithms"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function `search_insert(nums, target)` that returns the index of a target value if found, or the index where it should be inserted.",
      explanation: "Binary searching scales smoothly to narrow target placement locations in O(log N) operations.",
      hint: "The lower pointer index tracks the insertion index when searches terminate without direct matches.",
      starterCode: `def search_insert(nums, target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 3, 5, 6], 5", call: "search_insert([1, 3, 5, 6], 5)", expected: "2" },
        { input: "[1, 3, 5, 6], 2", call: "search_insert([1, 3, 5, 6], 2)", expected: "1" }
      ],
    },
    {
      id: 18,
      title: "Merge K Sorted Multilists Tracker",
      slug: "python-merge-k-sorted-multilists-tracker",
      seoTitle: "Python Merge K Sorted Lists Min Heap | Repractiq",
      metaDescription: "Merge multiple pre-sorted sequences into a unified sorted array using min-heaps.",
      tags: ["heaps", "sorting"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `merge_k_lists(lists)` that combines `k` pre-sorted collection tracks into a single sorted list.",
      explanation: "Min-heaps efficiently track the smallest active elements across all lists, keeping runtime bounded at O(N log K).",
      hint: "Use Python's built-in `heapq` module to track matching stream heads dynamically.",
      starterCode: `import heapq\n\ndef merge_k_lists(lists):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[[1, 4, 5], [1, 3, 4], [2, 6]]", call: "merge_k_lists([[1, 4, 5], [1, 3, 4], [2, 6]])", expected: "[1, 1, 2, 3, 4, 4, 5, 6]" }
      ],
    },
    {
      id: 19,
      title: "Longest Valid Parentheses Depth",
      slug: "python-longest-valid-parentheses-depth",
      seoTitle: "Python Longest Valid Substring Stack | Repractiq",
      metaDescription: "Calculate the max continuous substring length forming fully balanced combinations.",
      tags: ["stacks", "algorithms"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `longest_valid_parentheses(s)` that determines the maximum span of adjacent properly matching bracket indices.",
      explanation: "Push indices onto your stack to establish clear sequence boundaries for calculating lengths.",
      hint: "Initialize the stack with `-1` to handle edge configurations starting at the first index.",
      starterCode: `def longest_valid_parentheses(s):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'(()'", call: "longest_valid_parentheses('(()')", expected: "2" },
        { input: "')()())'", call: "longest_valid_parentheses(')()())')", expected: "4" }
      ],
    },
    {
      id: 20,
      title: "Find Duplicate Constant Space",
      slug: "python-find-duplicate-constant-space",
      seoTitle: "Python Duplicate Detection Tortoise Hare | Repractiq",
      metaDescription: "Find duplicate entries in read-only arrays using Floyd's Cycle-Finding algorithm for O(1) space.",
      tags: ["arrays", "algorithms"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `find_duplicate_constant(nums)` that identifies repeated entries in a read-only list without modifying the collection or using extra memory.",
      explanation: "Treat array values as index pointers to find cyclic loop intersections using fast and slow tracking pointers.",
      hint: "Apply the Tortoise and Hare approach to find where the pointer paths overlap.",
      starterCode: `def find_duplicate_constant(nums):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 3, 4, 2, 2]", call: "find_duplicate_constant([1, 3, 4, 2, 2])", expected: "2" }
      ],
    },
    {
      id: 21,
      title: "FizzBuzz Custom Mapping Rule Engine",
      slug: "python-fizzbuzz-custom-mapping-rule-engine",
      seoTitle: "Python Dynamic FizzBuzz Mappings | Repractiq",
      metaDescription: "Build modular custom FizzBuzz processing engines using flexible key/value string dictionary models.",
      tags: ["dictionaries", "loops"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `custom_fizzbuzz(n, rules_dict)` that generates dynamic string replacements based on divisibility rules defined in a dictionary.",
      explanation: "Iterating through structured rule collections makes it easy to add or change replacement conditions without altering the core function code.",
      hint: "Append matching string values from sorted divisor keys onto an active text container.",
      starterCode: `def custom_fizzbuzz(n, rules_dict):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "15, {3: 'Fizz', 5: 'Buzz'}", call: "custom_fizzbuzz(15, {3: 'Fizz', 5: 'Buzz'})", expected: "'FizzBuzz'" },
        { input: "7, {3: 'Fizz', 5: 'Buzz'}", call: "custom_fizzbuzz(7, {3: 'Fizz', 5: 'Buzz'})", expected: "'7'" }
      ],
    },
    {
      id: 22,
      title: "Fibonacci Memoized Cache Matrix",
      slug: "python-fibonacci-memoized-cache-matrix",
      seoTitle: "Python Recursive Fibonacci Memoization | Repractiq",
      metaDescription: "Build high-performance recursive Fibonacci engines using simple decorator storage caches.",
      tags: ["decorators", "advanced recursion"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `fib_memo(n)` that calculates recursive values quickly by caching subproblem results.",
      explanation: "Caching subproblem answers prevents redundant computations, optimizing recursive execution from exponential O(2^N) to linear O(N) time.",
      hint: "You can use Python's built-in `functools.lru_cache` to handle memoization automatically.",
      starterCode: `from functools import lru_cache\n\ndef fib_memo(n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "50", call: "str(fib_memo(50))", expected: "'12586269025'" }
      ],
    },
    {
      id: 23,
      title: "Palindrome Number Without Conversion",
      slug: "python-palindrome-number-without-conversion",
      seoTitle: "Python Integer Palindrome Reversal Math | Repractiq",
      metaDescription: "Determine if an integer is a palindrome mathematically without converting it to a string.",
      tags: ["math"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `is_number_palindrome(x)` that checks if integer inputs are palindromes without using string conversion.",
      explanation: "Reverse the integer mathematically by using modulo arithmetic and tracking the reconstructed remainder.",
      hint: "Negative numbers are not palindromes because of their trailing minus sign.",
      starterCode: `def is_number_palindrome(x):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "121", call: "is_number_palindrome(121)", expected: "True" },
        { input: "-121", call: "is_number_palindrome(-121)", expected: "False" }
      ],
    },
    {
      id: 24,
      title: "Find All Anagrams in a String",
      slug: "python-find-all-anagrams-in-string",
      seoTitle: "Python Sliding Window Anagram Tracking | Repractiq",
      metaDescription: "Find the starting indices of all anagram substrings using a sliding window and character maps.",
      tags: ["strings", "arrays"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `find_anagrams(s, p)` that finds all starting indices of `p`'s anagrams inside `s`.",
      explanation: "Maintain a sliding window equal to `p`'s length, updating character frequency counts as the window shifts across `s`.",
      hint: "Compare character count arrays or dictionaries instead of re-sorting substrings at every step.",
      starterCode: `def find_anagrams(s, p):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'cbaebabacd', 'abc'", call: "find_anagrams('cbaebabacd', 'abc')", expected: "[0, 6]" }
      ],
    },
    {
      id: 25,
      title: "Three Sum Zero Combinations",
      slug: "python-three-sum-zero-combinations",
      seoTitle: "Python Three Sum Array Combinations | Repractiq",
      metaDescription: "Find all unique triplets in an array that sum up to zero using sorting and two pointers.",
      tags: ["arrays", "sorting"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `three_sum(nums)` that returns all unique triplets `[nums[i], nums[j], nums[k]]` such that `i != j != k` and their values sum to zero.",
      explanation: "Sort the array first, then use a loop combined with two pointers while skipping duplicate values to find unique solutions.",
      hint: "Sort the list first so you can easily skip duplicate elements during pointer updates.",
      starterCode: `def three_sum(nums):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[-1, 0, 1, 2, -1, -4]", call: "str(sorted([sorted(x) for x in three_sum([-1, 0, 1, 2, -1, -4])]))", expected: "[[-1, -1, 2], [-1, 0, 1]]" }
      ],
    },
    {
      id: 26,
      title: "Detect Cycle in Linked Data Simulation",
      slug: "python-detect-cycle-in-linked-data-simulation",
      seoTitle: "Python Linked List Cycle Reference Checking | Repractiq",
      metaDescription: "Detect cyclic loops in reference networks using fast and slow tracking pointers.",
      tags: ["linked lists", "algorithms"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `has_cycle_simulation(edges, start)` that detects circular reference pathways in node connections.",
      explanation: "Use two tracking pointers moving at different speeds to identify cyclic loops when they eventually overlap.",
      hint: "If the fast pointer reaches the end of the collection, the graph or list contains no loops.",
      starterCode: `def has_cycle_simulation(edges, start):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{1: 2, 2: 3, 3: 1}, 1", call: "has_cycle_simulation({1: 2, 2: 3, 3: 1}, 1)", expected: "True" }
      ],
    },
    {
      id: 27,
      title: "Search in Rotated Sorted Array",
      slug: "python-search-in-rotated-sorted-array",
      seoTitle: "Python Rotated Array Binary Search | Repractiq",
      metaDescription: "Find elements in a rotated sorted array in logarithmic time by checking which half remains ordered.",
      tags: ["arrays", "algorithms"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `search_rotated(nums, target)` that finds a value within a sorted array that has been rotated at an unknown pivot.",
      explanation: "Identify which half of the divided array is correctly sorted to determine where to continue your binary search.",
      hint: "Check if `nums[left] <= nums[mid]` to find out if the left half is properly sorted.",
      starterCode: `def search_rotated(nums, target):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[4, 5, 6, 7, 0, 1, 2], 0", call: "search_rotated([4, 5, 6, 7, 0, 1, 2], 0)", expected: "4" },
        { input: "[4, 5, 6, 7, 0, 1, 2], 3", call: "search_rotated([4, 5, 6, 7, 0, 1, 2], 3)", expected: "-1" }
      ],
    },
    {
      id: 28,
      title: "Merge Interval Boundaries",
      slug: "python-merge-interval-boundaries",
      seoTitle: "Python Merge Overlapping Intervals | Repractiq",
      metaDescription: "Combine overlapping ranges into a continuous, simplified list of sorted intervals.",
      tags: ["arrays", "sorting"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `merge_intervals(intervals)` that merges all overlapping sub-ranges into a clean, non-overlapping array.",
      explanation: "Sort the intervals by their start times first, then iterate through to merge overlapping ranges.",
      hint: "An overlap occurs if the start time of the current interval is less than or equal to the end time of the previous one.",
      starterCode: `def merge_intervals(intervals):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[[1, 3], [2, 6], [8, 10], [15, 18]]", call: "merge_intervals([[1, 3], [2, 6], [8, 10], [15, 18]])", expected: "[[1, 6], [8, 10], [15, 18]]" }
      ],
    },
    {
      id: 29,
      title: "Minimum Remove to Make Valid Parentheses",
      slug: "python-minimum-remove-to-make-valid-parentheses",
      seoTitle: "Python Balance Parentheses String Correction | Repractiq",
      metaDescription: "Remove the minimum number of mismatched brackets to create a structurally valid string.",
      tags: ["stacks", "strings"],
      difficulty: "Medium",
      type: "function",
      description: "Write a function `min_remove_to_valid(s)` that removes the fewest parentheses possible to ensure all remaining brackets balance correctly.",
      explanation: "Track mismatched indices using a stack, then rebuild the string while skipping those invalid characters.",
      hint: "Convert the string to a list so you can easily modify characters at specific indices.",
      starterCode: `def min_remove_to_valid(s):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'lee(t(c)o)de)'", call: "min_remove_to_valid('lee(t(c)o)de)')", expected: "'lee(t(c)o)de'" }
      ],
    },
    {
      id: 30,
      title: "First Missing Positive Integer",
      slug: "python-first-missing-positive-integer",
      seoTitle: "Python First Missing Positive Array Index | Repractiq",
      metaDescription: "Find the smallest missing positive integer in an unsorted array in O(N) time and O(1) space.",
      tags: ["arrays", "algorithms"],
      difficulty: "Hard",
      type: "function",
      description: "Write a function `first_missing_positive(nums)` that finds the smallest missing positive integer from an unsorted list.",
      explanation: "Use the array itself as a hash table by swapping numbers into their correct index positions (`nums[i]` should match `i + 1`).",
      hint: "Place each number `x` at index `x - 1` using in-place swaps wherever possible.",
      starterCode: `def first_missing_positive(nums):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 0]", call: "first_missing_positive([1, 2, 0])", expected: "3" },
        { input: "[3, 4, -1, 1]", call: "first_missing_positive([3, 4, -1, 1])", expected: "2" }
      ],
    },
    {
        id: 31,
        title: "FizzBuzz Custom Bounds Matrix",
        slug: "python-fizzbuzz-custom-bounds-matrix",
        seoTitle: "Python FizzBuzz Variable Ranges | Repractiq",
        metaDescription: "Generate a targeted FizzBuzz sequence across customized start and end integer boundaries.",
        tags: ["loops", "conditionals"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `fizzbuzz_range(start, end)` that returns a list containing the FizzBuzz conversions for all integers from `start` to `end` inclusive.",
        explanation: "Standard loop evaluation mapped to flexible start/end parameters instead of hardcoded 1-to-N limits.",
        hint: "Ensure the loop structure accounts for the inclusive end boundary using `end + 1`.",
        starterCode: `def fizzbuzz_range(start, end):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "12, 16", call: "fizzbuzz_range(12, 16)", expected: "['Fizz', '13', '14', 'FizzBuzz', '16']" }
        ],
      },
      {
        id: 32,
        title: "Fibonacci Sum of Even Numbers",
        slug: "python-fibonacci-sum-of-even-numbers",
        seoTitle: "Python Even Fibonacci Summation | Repractiq",
        metaDescription: "Compute the combined total sum of all even-valued Fibonacci numbers that do not exceed a given upper limit.",
        tags: ["math", "loops"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `even_fibonacci_sum(limit)` that calculates the sum of all even-valued Fibonacci numbers whose values are less than or equal to `limit`.",
        explanation: "Iterate through the Fibonacci sequence terms, validating elements using modulo 2 checks and maintaining a running tracker.",
        hint: "Track the current value directly and break the iteration when the value exceeds the target limit.",
        starterCode: `def even_fibonacci_sum(limit):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "10", call: "even_fibonacci_sum(10)", expected: "10" },
          { input: "100", call: "even_fibonacci_sum(100)", expected: "44" }
        ],
      },
      {
        id: 33,
        title: "Valid Palindrome with Single Character Removal",
        slug: "python-valid-palindrome-single-character-removal",
        seoTitle: "Python Palindrome Permutation Removal Check | Repractiq",
        metaDescription: "Determine if a string can form a valid palindrome by removing at most a single character.",
        tags: ["strings", "two-pointers"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `valid_palindrome_removal(s)` that returns True if the string `s` can be made into a palindrome by deleting at most one character.",
        explanation: "Use two pointers moving inward. When a mismatch occurs, test the validity of the two remaining substrings created by skipping either character.",
        hint: "A helper function checking exact symmetry makes verifying the split substrings clean.",
        starterCode: `def valid_palindrome_removal(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'aba'", call: "valid_palindrome_removal('aba')", expected: "True" },
          { input: "'abca'", call: "valid_palindrome_removal('abca')", expected: "True" },
          { input: "'abc'", call: "valid_palindrome_removal('abc')", expected: "False" }
        ],
      },
      {
        id: 34,
        title: "Minimum Window Anagram Substring",
        slug: "python-minimum-window-anagram-substring",
        seoTitle: "Python Min Window Anagram Substring | Repractiq",
        metaDescription: "Locate the smallest substring container that matches all character counts of an anagram pattern target.",
        tags: ["strings", "sliding-window"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `min_window_anagram(s, t)` that finds the minimum continuous window substring in `s` containing all characters of string `t`.",
        explanation: "Use a sliding window with two pointers to expand and contract the search area while matching character count requirements.",
        hint: "Expand the right pointer until the window is valid, then contract from the left to minimize its length.",
        starterCode: `def min_window_anagram(s, t):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'ADOBECODEBANC', 'ABC'", call: "min_window_anagram('ADOBECODEBANC', 'ABC')", expected: "'BANC'" }
        ],
      },
      {
        id: 35,
        title: "Two Sum Data Structure Design",
        slug: "python-two-sum-data-structure-design",
        seoTitle: "Python TwoSum Class Design Implementation | Repractiq",
        metaDescription: "Design and implement a structured data class that optimizes both stream additions and target sum lookup operations.",
        tags: ["advanced oop", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Implement a tracking system class or helper function structure simulated via `two_sum_stream(actions, values)` where 'add' stores a number and 'find' checks if any two stored numbers sum to the target.",
        explanation: "Internal hash maps provide O(1) time complexity for additions, while lookups iterate over keys to check for matching complements.",
        hint: "Store values as dictionary counts to correctly handle duplicate inputs.",
        starterCode: `def two_sum_stream(actions, values):\n    # Simulated class interaction: actions=['add', 'find'], values=[[5], [10]]\n    # Return array of results matching 'find' actions\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['add', 'add', 'find', 'find'], [[1], [3], [4], [5]]", call: "two_sum_stream(['add', 'add', 'find', 'find'], [[1], [3], [4], [5]])", expected: "[True, False]" }
        ],
      },
      {
        id: 36,
        title: "Find the Middle Node of a Linked List",
        slug: "python-find-middle-node-linked-list",
        seoTitle: "Python Linked List Middle Element | Repractiq",
        metaDescription: "Locate the exact middle value of a sequential list using a single-pass, two-pointer approach.",
        tags: ["linked lists", "algorithms"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `find_list_middle(node_values)` that returns the middle node value from a simulated single link collection. For even lengths, return the second middle element.",
        explanation: "Use a fast and slow pointer strategy. Moving one pointer at double speed automatically places the slow pointer at the exact midpoint when the loop finishes.",
        hint: "Advance the fast pointer by two steps and the slow pointer by one step at each iteration.",
        starterCode: `def find_list_middle(node_values):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3, 4, 5]", call: "find_list_middle([1, 2, 3, 4, 5])", expected: "3" },
          { input: "[1, 2, 3, 4, 5, 6]", call: "find_list_middle([1, 2, 3, 4, 5, 6])", expected: "4" }
        ],
      },
      {
        id: 37,
        title: "Search Range in a Sorted Array",
        slug: "python-search-range-sorted-array",
        seoTitle: "Python Find First and Last Position | Repractiq",
        metaDescription: "Find the starting and ending index positions of a target value in a sorted array in O(log N) time.",
        tags: ["arrays", "algorithms"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `search_range(nums, target)` that returns the starting and ending index boundaries of a target value within a sorted list.",
        explanation: "Run two separate binary searches: one adjusted to find the leftmost boundary and another for the rightmost boundary.",
        hint: "When you find a match, don't stop; look left or right to find the outermost matching index.",
        starterCode: `def search_range(nums, target):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[5, 7, 7, 8, 8, 10], 8", call: "search_range([5, 7, 7, 8, 8, 10], 8)", expected: "[3, 4]" },
          { input: "[5, 7, 7, 8, 8, 10], 6", call: "search_range([5, 7, 7, 8, 8, 10], 6)", expected: "[-1, -1]" }
        ],
      },
      {
        id: 38,
        title: "Merge Sorted Arrays In-Place",
        slug: "python-merge-sorted-arrays-in-place",
        seoTitle: "Python In-Place Array Merging | Repractiq",
        metaDescription: "Merge two sorted arrays into a single combined collection without using any extra tracking memory.",
        tags: ["arrays", "two-pointers"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `merge_inplace(nums1, m, nums2, n)` that merges `nums2` into `nums1` as a single sorted array. `nums1` has enough extra space at the end to hold all elements.",
        explanation: "Work backward from the end of both lists, comparing values and filling the padded space from right to left to prevent overwriting elements.",
        hint: "Initialize three tracking pointers: one at index `m-1`, one at `n-1`, and one at the end of the array `m+n-1`.",
        starterCode: `def merge_inplace(nums1, m, nums2, n):\n    # Modify nums1 in-place, return nums1 for verification\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3, 0, 0, 0], 3, [2, 5, 6], 3", call: "merge_inplace([1, 2, 3, 0, 0, 0], 3, [2, 5, 6], 3)", expected: "[1, 2, 2, 3, 5, 6]" }
        ],
      },
      {
        id: 39,
        title: "Score of a Valid Parentheses String",
        slug: "python-score-of-valid-parentheses-string",
        seoTitle: "Python Score Balance Brackets | Repractiq",
        metaDescription: "Calculate the total value score of balanced bracket configurations based on their nesting depth rules.",
        tags: ["stacks", "math"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `score_parentheses(s)` where `()` scores 1 point, adjacent combinations add together `A + B`, and nested combinations double the inner value `2 * A`.",
        explanation: "Use a stack to track structural depth values, updating scores dynamically as brackets open and close.",
        hint: "Pushing a zero onto the stack helps track structural changes whenever a new nesting level opens.",
        starterCode: `def score_parentheses(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'()'", call: "score_parentheses('()')", expected: "1" },
          { input: "'(())'", call: "score_parentheses('(())')", expected: "2" },
          { input: "'()()'", call: "score_parentheses('()()')", expected: "2" }
        ],
      },
      {
        id: 40,
        title: "Find Duplicates in Linear Time and Space",
        slug: "python-find-duplicates-linear-time-space",
        seoTitle: "Python Linear Duplicate Array Identification | Repractiq",
        metaDescription: "Identify all duplicate numbers within an array using list indices to track values in linear time.",
        tags: ["arrays", "dictionaries"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `find_all_duplicates(nums)` that returns an array of all integers that appear twice in an input collection.",
        explanation: "A simple pass using a tracking set or counts dictionary isolates duplicate entries in linear time.",
        hint: "Use collections.Counter or basic dictionary lookups to quickly check for duplicate values.",
        starterCode: `def find_all_duplicates(nums):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[4, 3, 2, 7, 8, 2, 3, 1]", call: "sorted(find_all_duplicates([4, 3, 2, 7, 8, 2, 3, 1]))", expected: "[2, 3]" }
        ],
      },
      {
        id: 41,
        title: "FizzBuzz Matrix Generator Comprehension",
        slug: "python-fizzbuzz-matrix-generator-comprehension",
        seoTitle: "Python One Liner FizzBuzz Comprehension | Repractiq",
        metaDescription: "Generate a full FizzBuzz output sequence using a single optimized inline list comprehension.",
        tags: ["list comprehensions", "conditionals"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `fizzbuzz_comprehension(n)` that returns the FizzBuzz sequence up to `n` using a single inline list comprehension statement.",
        explanation: "Inline ternary expressions allow you to map multiple conditional paths inside a single, high-performance comprehension expression.",
        hint: "Chain ternary statements format: `'FizzBuzz' if i % 15 == 0 else ('Fizz' if i % 3 == 0 else ...)`.",
        starterCode: `def fizzbuzz_comprehension(n):\n    # Must return a single list comprehension expression\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "3", call: "fizzbuzz_comprehension(3)", expected: "['1', '2', 'Fizz']" }
        ],
      },
      {
        id: 42,
        title: "Fibonacci Matrix Exponentiation Scaling",
        slug: "python-fibonacci-matrix-exponentiation-scaling",
        seoTitle: "Python Matrix Fibonacci Exponentiation | Repractiq",
        metaDescription: "Compute large Fibonacci numbers in O(log N) time using matrix transformation exponentiation scaling.",
        tags: ["math", "advanced recursion"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `fib_matrix(n)` that calculates the n-th Fibonacci number using matrix multiplication transformations to maximize performance.",
        explanation: "Multiplying transformation matrices allows you to compute numbers in logarithmic time by squaring state factors.",
        hint: "The base transformation matrix is defined as `[[1, 1], [1, 0]]`.",
        starterCode: `def fib_matrix(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "9", call: "fib_matrix(9)", expected: "34" }
        ],
      },
      {
        id: 43,
        title: "Partition Labels Palindromic Constraints",
        slug: "python-partition-labels-palindromic-constraints",
        seoTitle: "Python Segment Partition Window Tracking | Repractiq",
        metaDescription: "Split a string into the maximum number of structural segments where each character appears in only one partition.",
        tags: ["strings", "greedy"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `partition_labels(s)` that divides a string into as many parts as possible so that each letter appears in at most one segment, returning a list of the segment lengths.",
        explanation: "Track the last occurrence of each character in a dictionary, then use a greedy strategy to expand the segment boundary as you find later occurrences.",
        hint: "Update your active segment boundary index using `max(end_index, last_seen[char])`.",
        starterCode: `def partition_labels(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'ababcbacadefegdehijhklij'", call: "partition_labels('ababcbacadefegdehijhklij')", expected: "[9, 7, 8]" }
        ],
      },
      {
        id: 44,
        title: "Anagram Prime Product Grouping Engine",
        slug: "python-anagram-prime-product-grouping-engine",
        seoTitle: "Python Anagram Prime Factor Multiplying | Repractiq",
        metaDescription: "Group anagram word collections by assigning unique prime product hashes to each letter configuration.",
        tags: ["strings", "math"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `prime_group_anagrams(strs)` that groups anagrams together by calculating a unique numeric key for each word based on prime numbers assigned to each letter.",
        explanation: "The fundamental theorem of arithmetic guarantees that the product of prime factors is unique for any combination of characters, providing an alternative to string sorting.",
        hint: "Map the 26 lowercase English letters to the first 26 prime numbers.",
        starterCode: `def prime_group_anagrams(strs):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['eat', 'tea', 'tan']", call: "[sorted(x) for x in prime_group_anagrams(['eat', 'tea', 'tan'])]", expected: "[['eat', 'tea'], ['tan']]" }
        ],
      },
      {
        id: 45,
        title: "Four Sum Target Vector Sets",
        slug: "python-four-sum-target-vector-sets",
        seoTitle: "Python Four Sum Quadruplet Tracking | Repractiq",
        metaDescription: "Locate all unique quadruplet combinations in an unsorted array that sum up to a specific target.",
        tags: ["arrays", "sorting"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `four_sum(nums, target)` that finds all unique quadruplets `[a, b, c, d]` that sum exactly to the target value.",
        explanation: "Chain two loops together to fix the first two numbers, then use two pointers to find the remaining pair while skipping duplicate values.",
        hint: "Sort the array first to make skipping duplicate values straightforward across all pointer locations.",
        starterCode: `def four_sum(nums, target):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 0, -1, 0, -2, 2], 0", call: "str(sorted([sorted(x) for x in four_sum([1, 0, -1, 0, -2, 2], 0)]))", expected: "[[-2, -1, 1, 2], [-2, 0, 0, 2], [-1, 0, 0, 1]]" }
        ],
      },
      {
        id: 46,
        title: "Remove N-th Node From End of Simulated List",
        slug: "python-remove-nth-node-from-end",
        seoTitle: "Python Remove Linked List Node Fast Slow | Repractiq",
        metaDescription: "Remove the n-th node from the end of a linked list in a single pass using two tracking pointers.",
        tags: ["linked lists", "algorithms"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `remove_nth_from_end(nodes, n)` that removes the node located `n` positions from the end of a simulated sequence list.",
        explanation: "Advance a lead pointer `n` steps ahead of a follow pointer. When the lead pointer reaches the end of the list, the follow pointer will be positioned just before the target node to remove.",
        hint: "Using a dummy head node helps handle edge cases smoothly, such as removing the very first element in the list.",
        starterCode: `def remove_nth_from_end(nodes, n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3, 4, 5], 2", call: "remove_nth_from_end([1, 2, 3, 4, 5], 2)", expected: "[1, 2, 3, 5]" }
        ],
      },
      {
        id: 47,
        title: "Find Minimum in Rotated Sorted Array",
        slug: "python-find-minimum-rotated-sorted-array",
        seoTitle: "Python Find Rotated Minimum Binary Search | Repractiq",
        metaDescription: "Find the smallest value in a rotated sorted array in O(log N) time using binary search logic.",
        tags: ["arrays", "algorithms"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `find_min_rotated(nums)` that finds the minimum element within an ordered list that has been rotated at a random pivot point.",
        explanation: "Compare the middle value with the rightmost boundary value to determine which side contains the rotation inflection point.",
        hint: "If `nums[mid] > nums[right]`, the minimum element lies in the right half of the array.",
        starterCode: `def find_min_rotated(nums):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[3, 4, 5, 1, 2]", call: "find_min_rotated([3, 4, 5, 1, 2])", expected: "1" },
          { input: "[4, 5, 6, 7, 0, 1, 2]", call: "find_min_rotated([4, 5, 6, 7, 0, 1, 2])", expected: "0" }
        ],
      },
      {
        id: 48,
        title: "Insert Interval Segment Merge",
        slug: "python-insert-interval-segment-merge",
        seoTitle: "Python Insert and Merge Overlapping Intervals | Repractiq",
        metaDescription: "Insert a new range interval into a sorted collection, merging any overlapping segments automatically.",
        tags: ["arrays", "sorting"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `insert_interval(intervals, new_interval)` that adds the new range segment while keeping the final list sorted and free of overlapping intervals.",
        explanation: "Iterate through the sorted ranges, appending non-overlapping segments before the new interval, combining all overlapping segments, and then appending the remaining trailing intervals.",
        hint: "Combine overlapping segments by updating the boundaries: `[min(start1, start2), max(end1, end2)]`.",
        starterCode: `def insert_interval(intervals, new_interval):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 3], [6, 9]], [2, 5]", call: "insert_interval([[1, 3], [6, 9]], [2, 5])", expected: "[[1, 5], [6, 9]]" }
        ],
      },
      {
        id: 49,
        title: "Check If Parentheses Path Can Be Valid",
        slug: "python-check-parentheses-path-valid",
        seoTitle: "Python Dynamic Wildcard Parentheses Checking | Repractiq",
        metaDescription: "Determine if bracket sequences containing dynamic wildcard tokens can form valid balanced paths.",
        tags: ["strings", "greedy"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `can_be_valid(s, locked)` that checks if a bracket string can be balanced, where elements marked as unlocked (`0`) can be changed to either opening or closing brackets.",
        explanation: "Perform two balance checks (left-to-right and right-to-left) to verify that the total count of open or highly flexible brackets never falls below zero.",
        hint: "The total length of the string must be an even number to form balanced pairs.",
        starterCode: `def can_be_valid(s, locked):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'))))', '0011'", call: "can_be_valid('))))', '0011')", expected: "False" }
        ],
      },
      {
        id: 50,
        title: "Find Duplicate in Array via Bit Masking",
        slug: "python-find-duplicate-array-bit-masking",
        seoTitle: "Python Bitwise Duplicate Value Extraction | Repractiq",
        metaDescription: "Identify duplicate number flags inside a positive array collection using bit shifting masks.",
        tags: ["math", "performance optimization"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `find_duplicate_bitmask(nums)` that checks an integer list and extracts duplicate values using bit shifting masks instead of tracking sets.",
        explanation: "Use an integer as a bit vector. Shift bits by the value of each number to detect duplicates when a bit position is triggered a second time.",
        hint: "Use the bitwise AND operation `bit_tracker & (1 << num)` to check if a value was previously seen.",
        starterCode: `def find_duplicate_bitmask(nums):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 3, 2, 4, 3]", call: "find_duplicate_bitmask([1, 3, 2, 4, 3])", expected: "3" }
        ],
      },
      {
        id: 51,
        title: "FizzBuzz Multi-Threaded Sync Simulation",
        slug: "python-fizzbuzz-multi-threaded-sync-simulation",
        seoTitle: "Python Multi-Threaded FizzBuzz Logic | Repractiq",
        metaDescription: "Simulate thread synchronization processing constraints for a cooperative FizzBuzz output runner.",
        tags: ["async basics", "loops"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `simulated_threaded_fizzbuzz(n)` that coordinates four separate execution steps ('fizz', 'buzz', 'fizzbuzz', 'number') to generate a combined, properly ordered FizzBuzz sequence list.",
        explanation: "Coordinate text additions based on matching divisibility checks to simulate multi-threaded synchronization patterns.",
        hint: "Use loop step counters to determine which processing step is responsible for adding the current number.",
        starterCode: `def simulated_threaded_fizzbuzz(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "5", call: "simulated_threaded_fizzbuzz(5)", expected: "['1', '2', 'Fizz', '4', 'Buzz']" }
        ],
      },
      {
        id: 52,
        title: "Fibonacci Golden Ratio Fast Formula",
        slug: "python-fibonacci-golden-ratio-fast-formula",
        seoTitle: "Python Binet Formula Fibonacci Calculation | Repractiq",
        metaDescription: "Calculate the n-th Fibonacci number in O(1) constant time using Binet's Golden Ratio mathematical formula.",
        tags: ["math"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `fib_golden_ratio(n)` that returns the n-th Fibonacci number in constant time using Binet's analytical formula.",
        explanation: "Binet's formula uses the golden ratio constant phi to calculate specific sequence values directly without loops or recursion.",
        hint: "Round the floating-point result using `int(round(result))` to correct for minor precision limits.",
        starterCode: `import math\n\ndef fib_golden_ratio(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "10", call: "fib_golden_ratio(10)", expected: "55" }
        ],
      },
      {
        id: 53,
        title: "Shortest Palindrome Prefix Additions",
        slug: "python-shortest-palindrome-prefix-additions",
        seoTitle: "Python Shortest Palindrome Conversion | Repractiq",
        metaDescription: "Convert any string into a palindrome by adding the minimum number of characters at the front.",
        tags: ["strings", "algorithms"],
        difficulty: "Hard",
        type: "function",
        description: "Write a function `shortest_palindrome_prefix(s)` that finds the minimum number of characters to add to the front of `s` to transform it into a valid palindrome.",
        explanation: "Find the longest palindromic prefix already in the string, then reverse the remaining suffix and append it to the front.",
        hint: "You can use string matching algorithms or reversed string comparisons to isolate the longest valid prefix.",
        starterCode: `def shortest_palindrome_prefix(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'aacecaaa'", call: "shortest_palindrome_prefix('aacecaaa')", expected: "'aacecaaa'" },
          { input: "'abcd'", call: "shortest_palindrome_prefix('abcd')", expected: "'dcbabcd'" }
        ],
      },
      {
        id: 54,
        title: "Anagram Substring Frequency Maps",
        slug: "python-anagram-substring-frequency-maps",
        seoTitle: "Python Anagram Counter Dictionary Search | Repractiq",
        metaDescription: "Count how many substrings match the character frequencies of a target search pattern.",
        tags: ["strings", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `count_anagram_substrings(text, pattern)` that counts how many continuous substrings in `text` match the exact character frequency profile of `pattern`.",
        explanation: "Use a sliding window map to check character distribution blocks across the main text sequence in linear time.",
        hint: "Convert character frequency maps into tuples or directly compare frequency dictionaries inside the window loop.",
        starterCode: `from collections import Counter\n\ndef count_anagram_substrings(text, pattern):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'cbaebabacd', 'abc'", call: "count_anagram_substrings('cbaebabacd', 'abc')", expected: "2" }
        ],
      },
      {
        id: 55,
        title: "Two Sum Data Subtraction Index Pairs",
        slug: "python-two-sum-data-subtraction-index-pairs",
        seoTitle: "Python Two Sum Index Absolute Differences | Repractiq",
        metaDescription: "Find index pairs matching target sums that maximize the absolute coordinate distances between the indices.",
        tags: ["arrays", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `two_sum_max_distance(nums, target)` that returns the indices of two elements summing to `target` that have the maximum possible absolute index difference.",
        explanation: "Store the earliest seen index for each number in a dictionary, updating the maximum distance whenever a valid matching complement is found later in the array.",
        hint: "Track the first occurrence of each element to ensure you get the widest possible index gap.",
        starterCode: `def two_sum_max_distance(nums, target):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[2, 7, 11, 7, 2], 9", call: "two_sum_max_distance([2, 7, 11, 7, 2], 9)", expected: "[0, 4]" }
        ],
      },
      {
        id: 56,
        title: "Merge Two Sorted Linked Substructures",
        slug: "python-merge-two-sorted-linked-substructures",
        seoTitle: "Python Merge Recursive Sorted List Node | Repractiq",
        metaDescription: "Combine two sorted linked lists into one continuous sorted chain using recursive node relationships.",
        tags: ["linked lists", "advanced recursion"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `merge_nodes_recursive(l1, l2)` that combines two simulated list configurations using recursive pointer routing logic.",
        explanation: "Compare the head elements of both lists, then recursively link the smaller element to the merged result of the remaining elements.",
        hint: "If either list is empty, return the remaining elements of the other list directly.",
        starterCode: `def merge_nodes_recursive(l1, l2):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 3, 5], [2, 4, 6]", call: "merge_nodes_recursive([1, 3, 5], [2, 4, 6])", expected: "[1, 2, 3, 4, 5, 6]" }
        ],
      },
      {
        id: 57,
        title: "Find Peak Element via Binary Search",
        slug: "python-find-peak-element-binary-search",
        seoTitle: "Python Find Peak Element Binary Search | Repractiq",
        metaDescription: "Locate a local peak element that is strictly greater than its adjacent neighbors in O(log N) time.",
        tags: ["arrays", "algorithms"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `find_peak_element(nums)` that finds a local peak element—an item that is greater than its immediate neighbors—and returns its index location.",
        explanation: "Use a binary search strategy. If the middle element is smaller than its right neighbor, a peak is guaranteed to exist in the right half of the array.",
        hint: "Compare `nums[mid]` against `nums[mid + 1]` to decide which direction to move your search window.",
        starterCode: `def find_peak_element(nums):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3, 1]", call: "find_peak_element([1, 2, 3, 1])", expected: "2" }
        ],
      },
      {
        id: 58,
        title: "Interval Intersection Matrix Cores",
        slug: "python-interval-intersection-matrix-cores",
        seoTitle: "Python List Interval Intersection Finder | Repractiq",
        metaDescription: "Find all overlapping region intersections between two separate sets of sorted intervals.",
        tags: ["arrays", "two-pointers"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `interval_intersections(listA, listB)` that computes the overlapping regions between two separate collections of sorted intervals.",
        explanation: "Use two pointers to compare intervals from both lists simultaneously, calculating the intersection boundaries and advancing the pointer pointing to the interval that ends earlier.",
        hint: "The intersection range is determined by `[max(startA, startB), min(endA, endB)]`.",
        starterCode: `def interval_intersections(listA, listB):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[0, 2], [5, 10]], [[1, 5]]", call: "interval_intersections([[0, 2], [5, 10]], [[1, 5]])", expected: "[[1, 2], [5, 5]]" }
        ],
      },
      {
        id: 59,
        title: "Validate Bracket Depth String Sequences",
        slug: "python-validate-bracket-depth-string-sequences",
        seoTitle: "Python Maximum Nesting Depth Counter | Repractiq",
        metaDescription: "Calculate the maximum nesting depth layer reached by properly balanced bracket sequences.",
        tags: ["stacks", "strings"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `max_depth_parentheses(s)` that measures the deepest nesting layer reached by balanced parentheses within a text string.",
        explanation: "Increment a counter when an opening bracket is found and decrement it for closing brackets, tracking the highest counter value reached.",
        hint: "You can implement this with a single running counter instead of using a full stack object.",
        starterCode: `def max_depth_parentheses(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'(1+(2*3)+((8)/4))'", call: "max_depth_parentheses('(1+(2*3)+((8)/4))')", expected: "3" }
        ],
      },
      {
        id: 60,
        title: "Find Duplicate Files via Dynamic Hashes",
        slug: "python-find-duplicate-files-dynamic-hashes",
        seoTitle: "Python Group Duplicate Content Hashes | Repractiq",
        metaDescription: "Group duplicate file paths together based on their structural content strings using hash maps.",
        tags: ["dictionaries", "strings"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `find_duplicate_files(paths)` that groups paths containing identical text contents together, returning lists of duplicate file locations.",
        explanation: "Parse the content blocks out of the path strings to use as dictionary keys, appending the corresponding file paths to matching key buckets.",
        hint: "Use string split functions to separate the file name from its text content bracket payload.",
        starterCode: `def find_duplicate_files(paths):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['root/a 1.txt(abcd) 2.txt(efgh)', 'root/c 3.txt(abcd)']", call: "[sorted(x) for x in find_duplicate_files(['root/a 1.txt(abcd) 2.txt(efgh)', 'root/c 3.txt(abcd)'])]", expected: "[['root/a/1.txt', 'root/c/3.txt']]" }
        ],
      },
      {
        id: 61,
        title: "Safe File Reader with Fallback Value",
        slug: "python-safe-file-reader-fallback",
        seoTitle: "Python Safe File Content Reader with Fallback | Repractiq",
        metaDescription: "Read text files safely with built-in exception handling and customizable default fallback values.",
        tags: ["file handling", "error handling"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `safe_read_file(filename, fallback)` that attempts to open and read the contents of a text file. If the file does not exist, it should catch the error and return the `fallback` value instead.",
        explanation: "Using try-except blocks with FileNotFoundError prevents code execution crashes when addressing missing storage dependencies.",
        hint: "Wrap the standard open() statement within a try block and catch FileNotFoundError.",
        starterCode: `def safe_read_file(filename, fallback):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'missing.txt', 'Default Content'", call: "safe_read_file('missing.txt', 'Default Content')", expected: "'Default Content'" }
        ],
      },
      {
        id: 62,
        title: "Custom Data Serialization Validator",
        slug: "python-custom-data-serialization-validator",
        seoTitle: "Python Type Validation Custom Error Handling | Repractiq",
        metaDescription: "Raise structural ValueError exceptions when validating data payload properties manually.",
        tags: ["error handling", "conditionals"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `validate_payload(data)` that checks if a dictionary contains the keys 'id' and 'token'. If either key is missing or 'id' is not a positive integer, explicitly raise a `ValueError` with a descriptive error message. Return True if valid.",
        explanation: "Manual error triggering via the raise keyword enforces interface guidelines before execution passes to lower system parts.",
        hint: "Use `isinstance(data.get('id'), int)` along with strict dictionary key inclusion checks.",
        starterCode: `def validate_payload(data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'id': -5, 'token': 'abc'}", call: "try:\\n    validate_payload({'id': -5, 'token': 'abc'})\\nexcept ValueError:\\n    print('Invalid')", expected: "Invalid" }
        ],
      },
      {
        id: 63,
        title: "Bank Account Encapsulation Wrapper",
        slug: "python-bank-account-encapsulation-wrapper",
        seoTitle: "Python Object Property Encapsulation Design | Repractiq",
        metaDescription: "Build balanced object class interfaces that shelter vital tracking states using structured access modifiers.",
        tags: ["oop basics"],
        difficulty: "Medium",
        type: "function",
        description: "Implement a simulated balance execution function `simulate_account(initial_balance, deposit_amt, withdraw_amt)` that uses an internal class protecting balance via private properties, allowing modifications only through clear deposit and withdraw methods. Return the final balance.",
        explanation: "Prefixing structural parameters with double underscores triggers name mangling, shielding variables from external tampering.",
        hint: "Implement verification inside the withdrawal method to prevent the balance from dropping below zero.",
        starterCode: `def simulate_account(initial_balance, deposit_amt, withdraw_amt):\n    # Simulate class instantiation and action flows inside\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "100, 50, 30", call: "simulate_account(100, 50, 30)", expected: "120" }
        ],
      },
      {
        id: 64,
        title: "Lambda Dynamic Multiplier Matrix",
        slug: "python-lambda-dynamic-multiplier-matrix",
        seoTitle: "Python Higher-Order Lambda Functions | Repractiq",
        metaDescription: "Generate inline function multipliers dynamically using nested anonymous lambda functions.",
        tags: ["lambda functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `make_multiplier(n)` that returns an anonymous lambda function. The returned lambda function should take a single argument `x` and return the product of `x` and `n`.",
        explanation: "Higher-order functional closures capture parameters from outer contexts, yielding distinct inline execution formulas.",
        hint: "Return a statement structure configured as: `lambda x: x * n`.",
        starterCode: `def make_multiplier(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "3", call: "make_multiplier(3)(5)", expected: "15" }
        ],
      },
      {
        id: 65,
        title: "Extract Long Emails via Functional Map",
        slug: "python-extract-long-emails-map-filter",
        seoTitle: "Python Map and Filter Processing Chains | Repractiq",
        metaDescription: "Filter and transform collection inputs using structured functional programming chains.",
        tags: ["map/filter", "lambda functions"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `process_emails(email_list)` that uses `filter` to keep emails longer than 10 characters, and `map` to convert those remaining valid items entirely to lowercase. Return a list.",
        explanation: "Combining map and filter operations maps transformations across sequences efficiently without relying on extensive loop matrices.",
        hint: "Convert the lazy evaluation outputs explicitly into a list block structure using `list()` before returning.",
        starterCode: `def process_emails(email_list):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['A@B.COM', 'LongUserEmail@domain.com', 'short@c.cc']", call: "process_emails(['A@B.COM', 'LongUserEmail@domain.com', 'short@c.cc'])", expected: "['longuseremail@domain.com', 'short@c.cc']" }
        ],
      },
      {
        id: 66,
        title: "Flatten Complex Nested Coordinate Maps",
        slug: "python-flatten-nested-coordinate-maps",
        seoTitle: "Python Flatten Nested Lists and Dictionaries | Repractiq",
        metaDescription: "Extract hidden structural properties nested within hybrid list-dictionary collection matrices.",
        tags: ["nested data structures"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `flatten_locations(data)` that accepts a dictionary mapping regions to lists of location dictionaries, each containing a 'name' key. Extract all location names into a flat, sorted list.",
        explanation: "Iterating through multi-tiered collections lets you consolidate buried data records into flat collections for analysis.",
        hint: "Use nested loops or list comprehensions to unpack items inside the inner lists.",
        starterCode: `def flatten_locations(data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'North': [{'name': 'Boston'}, {'name': 'NY'}], 'South': [{'name': 'Austin'}]}", call: "flatten_locations({'North': [{'name': 'Boston'}, {'name': 'NY'}], 'South': [{'name': 'Austin'}]})", expected: "['Austin', 'Boston', 'NY']" }
        ],
      },
      {
        id: 67,
        title: "Clean and Reformat Messy CSV Logs",
        slug: "python-clean-reformat-messy-csv-logs",
        seoTitle: "Python Clean Spaced Strings and Data Values | Repractiq",
        metaDescription: "Clean messy CSV string fields by tracking spacing anomalies and removing unexpected structural metadata.",
        tags: ["string manipulation"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `clean_csv_row(row_str)` that splits a comma-separated string, strips all whitespace from each extracted value, converts them to uppercase, and rejoins them with a semicolon separator.",
        explanation: "Chaining `.split()`, `.strip()`, and `.join()` transforms formatting safely across irregular logging entries.",
        hint: "Use list comprehensions to clean all individual entries together in a single pass.",
        starterCode: `def clean_csv_row(row_str):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "' apple , banana , cherry '", call: "clean_csv_row(' apple , banana , cherry ')", expected: "'APPLE;BANANA;CHERRY'" }
        ],
      },
      {
        id: 68,
        title: "Sort Complex Store Inventory by Value",
        slug: "python-sort-complex-store-inventory-value",
        seoTitle: "Python Sort Custom Keys Multi Attribute | Repractiq",
        metaDescription: "Sort multidimensional object lists using explicit custom lambda element criteria.",
        tags: ["sorting", "lambda functions"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sort_inventory(items)` that takes a list of dictionaries—where each item has a 'price' and a 'quantity'—and sorts them in descending order based on total inventory value (price * quantity).",
        explanation: "Passing custom function definitions into the key parameter of sorted() sorts complex data models cleanly.",
        hint: "Configure your lambda argument to calculate key weightings dynamically: `key=lambda x: x['price'] * x['quantity']`.",
        starterCode: `def sort_inventory(items):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'price': 10, 'quantity': 2}, {'price': 5, 'quantity': 10}]", call: "sort_inventory([{'price': 10, 'quantity': 2}, {'price': 5, 'quantity': 10}])", expected: "[{'price': 5, 'quantity': 10}, {'price': 10, 'quantity': 2}]" }
        ],
      },
      {
        id: 69,
        title: "Sum Digits of a Number via Recursion",
        slug: "python-sum-digits-number-recursion",
        seoTitle: "Python Recursive Digit Summation | Repractiq",
        metaDescription: "Calculate the mathematical sum of an integer's digits using recursive functional division.",
        tags: ["recursion basics"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sum_digits_recursive(n)` that calculates the sum of all numerical digits within a positive integer `n` using recursion.",
        explanation: "Extract digits sequentially using base-10 modulo operations, passing the remaining truncated quotient to the next recursive layer.",
        hint: "The base case stops processing when the remaining dividend drops to zero.",
        starterCode: `def sum_digits_recursive(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "1234", call: "sum_digits_recursive(1234)", expected: "10" },
          { input: "9", call: "sum_digits_recursive(9)", expected: "9" }
        ],
      },
      {
        id: 70,
        title: "Simulated Math Module Geometry Lookup",
        slug: "python-simulated-math-module-geometry",
        seoTitle: "Python Namespace Scope Math Access | Repractiq",
        metaDescription: "Simulate mathematical value mappings using built-in math module functionality.",
        tags: ["modules", "math"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `hypotenuse_length(a, b)` that imports Python's native `math` module internally and uses it to calculate the hypotenuse of a right-angled triangle given legs `a` and `b`.",
        explanation: "Importing modules inside a function controls scope, pulling in optimized formulas exactly where they are needed.",
        hint: "Use `math.sqrt()` or the built-in `math.hypot()` method to compute the result.",
        starterCode: `def hypotenuse_length(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "3, 4", call: "hypotenuse_length(3, 4)", expected: "5.0" }
        ],
      },
      {
        id: 71,
        title: "Count Specific Substrings across File Streams",
        slug: "python-count-substrings-file-streams",
        seoTitle: "Python Stream Buffer Content Matching | Repractiq",
        metaDescription: "Scan massive textual file streams to match occurrences of specified query strings safely.",
        tags: ["file handling", "string manipulation"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `count_word_in_simulated_file(file_content, target_word)` that simulates reading a multi-line document, counting every exact matching instance of `target_word` found across all lines.",
        explanation: "Splitting input data by newline patterns replicates line-by-line streaming, managing memory efficiently when working with large data stores.",
        hint: "Normalize string comparisons by using standard casing rules across all data entries.",
        starterCode: `def count_word_in_simulated_file(file_content, target_word):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'error: bad connection\\ninfo: success\\nerror: timeout', 'error'", call: "count_word_in_simulated_file('error: bad connection\\\\ninfo: success\\\\nerror: timeout', 'error')", expected: "2" }
        ],
      },
      {
        id: 72,
        title: "Safe Value Cast with Multi-Exception Safety Net",
        slug: "python-safe-value-cast-multi-exception",
        seoTitle: "Python Catch Diverse Runtime Multi Exceptions | Repractiq",
        metaDescription: "Cast dynamic types safely using robust exception frameworks that watch for multiple distinct error variations.",
        tags: ["error handling"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `safe_integer_cast(val)` that tries to convert an input to an integer. It must catch both `ValueError` and `TypeError`, returning -1 if either exception is encountered.",
        explanation: "Grouping multiple exception blocks under a single runtime handler ensures execution continues smoothly despite diverse data formatting issues.",
        hint: "Use `except (ValueError, TypeError):` to capture both matching validation exceptions together.",
        starterCode: `def safe_integer_cast(val):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'abc'", call: "safe_integer_cast('abc')", expected: "-1" },
          { input: "[1, 2]", call: "safe_integer_cast([1, 2])", expected: "-1" },
          { input: "'42'", call: "safe_integer_cast('42')", expected: "42" }
        ],
      },
      {
        id: 73,
        title: "Polymorphic Vehicle Rental Fee Matrix",
        slug: "python-polymorphic-vehicle-rental-fee",
        seoTitle: "Python OOP Polymorphism Method Overriding | Repractiq",
        metaDescription: "Design clear object structures that compute specific fee rates using polymorphic method overrides.",
        tags: ["oop basics"],
        difficulty: "Medium",
        type: "function",
        description: "Create a polymorphic structure simulated by `calculate_rental_cost(vehicle_type, days)` where base class methods are overridden by child configurations ('Car', 'Bike') to compute rates ($50/day vs $20/day).",
        explanation: "Polymorphism lets child subclasses redefine method behavior, enabling clean interfaces that process varied business entities uniformly.",
        hint: "Inherit a base class structural framework down to both unique child object setups.",
        starterCode: `def calculate_rental_cost(vehicle_type, days):\n    # Simulate Car and Bike class method dispatches\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Car', 3", call: "calculate_rental_cost('Car', 3)", expected: "150" },
          { input: "'Bike', 5", call: "calculate_rental_cost('Bike', 5)", expected: "100" }
        ],
      },
      {
        id: 74,
        title: "Dynamic Filter Engine via Variable Lambdas",
        slug: "python-dynamic-filter-engine-variable-lambdas",
        seoTitle: "Python Conditional Functional Filtering | Repractiq",
        metaDescription: "Filter array collections dynamically using variable validation criteria powered by custom lambdas.",
        tags: ["lambda functions", "map/filter"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `filter_by_condition(numbers, threshold, condition_type)` that returns numbers matching a condition configured by a lambda ('greater' than or 'less' than the threshold).",
        explanation: "Assigning lambdas to processing variables allows functional architectures to toggle business logic dynamically at runtime.",
        hint: "Define alternative conditional checks like `lambda x: x > threshold` dynamically based on the input string parameter.",
        starterCode: `def filter_by_condition(numbers, threshold, condition_type):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 5, 8, 2], 4, 'greater'", call: "filter_by_condition([1, 5, 8, 2], 4, 'greater')", expected: "[5, 8]" }
        ],
      },
      {
        id: 75,
        title: "Array Coordinate Normalizer via Map",
        slug: "python-array-coordinate-normalizer-map",
        seoTitle: "Python Linear Normalization Mapping | Repractiq",
        metaDescription: "Normalize data coordinates across numerical collection blocks using functional list map operations.",
        tags: ["map/filter"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `normalize_coordinates(coords, factor)` that updates a list of floating-point numbers by multiplying each element by `factor` and rounding to 2 decimal places using `map()`.",
        explanation: "Map functions apply mathematical normalizations consistently across numerical list vectors without requiring explicit counter tracking indices.",
        hint: "Use a lambda wrapper inside the map call: `map(lambda c: round(c * factor, 2), coords)`.",
        starterCode: `def normalize_coordinates(coords, factor):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1.234, 5.678], 2", call: "normalize_coordinates([1.234, 5.678], 2)", expected: "[2.47, 11.36]" }
        ],
      },
      {
        id: 76,
        title: "Group Nested Transactions by Category",
        slug: "python-group-nested-transactions-category",
        seoTitle: "Python Matrix Dictionary Content Grouping | Repractiq",
        metaDescription: "Parse complex nested object collections to organize financial records by their category property.",
        tags: ["nested data structures", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `group_transactions(transactions)` that processes a list of dictionaries—where each item has a 'category' and an 'amount'—and calculates the total amount spent per category.",
        explanation: "Iterating over nested record parameters allows you to easily aggregate data fields into structured metrics dashboards.",
        hint: "Initialize default category balance mappings using a dictionary or a collections.defaultdict.",
        starterCode: `def group_transactions(transactions):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'category': 'Food', 'amount': 15}, {'category': 'Tech', 'amount': 200}, {'category': 'Food', 'amount': 10}]", call: "dict(sorted(group_transactions([{'category': 'Food', 'amount': 15}, {'category': 'Tech', 'amount': 200}, {'category': 'Food', 'amount': 10}]).items()))", expected: "{'Food': 25, 'Tech': 200}" }
        ],
      },
      {
        id: 77,
        title: "Longest Word Extractor via Clean Regex Symbols",
        slug: "python-longest-word-extractor-regex",
        seoTitle: "Python String Cleaning Character Parsing | Repractiq",
        metaDescription: "Extract the longest words from unstructured text files by stripping punctuation symbols.",
        tags: ["string manipulation"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `find_longest_clean_word(sentence)` that removes common punctuation symbols (`!`, `,`, `.`, `?`) from a string, splits the text into words, and returns the longest word found.",
        explanation: "Stripping special character formatting from unstructured strings isolations text parameters cleanly for analytical sorting algorithms.",
        hint: "Replace punctuation characters with empty strings before running the split operation.",
        starterCode: `def find_longest_clean_word(sentence):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Wow, that is fantastic!'", call: "find_longest_clean_word('Wow, that is fantastic!')", expected: "'fantastic'" }
        ],
      },
      {
        id: 78,
        title: "Sort String Coordinates by Lexicographical Length",
        slug: "python-sort-string-coordinates-length",
        seoTitle: "Python Multi Tie Sorting Criteria | Repractiq",
        metaDescription: "Sort string collections based on word lengths, using alphabetical sorting as a fallback to resolve ties cleanly.",
        tags: ["sorting", "strings"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sort_by_length_and_alpha(words)` that sorts a list of strings primarily by their length in ascending order. If two strings have identical lengths, sort them alphabetically.",
        explanation: "Returning custom tuples from sorting functions enables multi-key prioritization workflows out-of-the-box.",
        hint: "Set the sorting key to return a multi-part tuple: `key=lambda w: (len(w), w)`.",
        starterCode: `def sort_by_length_and_alpha(words):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['cat', 'apple', 'bat', 'banana']", call: "sort_by_length_and_alpha(['cat', 'apple', 'bat', 'banana'])", expected: "['bat', 'cat', 'apple', 'banana']" }
        ],
      },
      {
        id: 79,
        title: "Recursive String Length Evaluator",
        slug: "python-recursive-string-length-evaluator",
        seoTitle: "Python Recursive Element Length Counting | Repractiq",
        metaDescription: "Calculate the exact total length of a string using recursive slicing instead of the built-in len() function.",
        tags: ["recursion basics", "strings"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `recursive_len(s)` that determines the total length of a string using recursive slicing without using the built-in `len()` function.",
        explanation: "Shifting through index locations sequentially allows tracking mechanisms to calculate total lengths using structural base cases.",
        hint: "The base case is an empty string `''`, which returns a length score of 0.",
        starterCode: `def recursive_len(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'hello'", call: "recursive_len('hello')", expected: "5" },
          { input: "''", call: "recursive_len('')", expected: "0" }
        ],
      },
      {
        id: 80,
        title: "Epoch Time Converter via Native Module Scope",
        slug: "python-epoch-time-converter-native-module",
        seoTitle: "Python Time Namespace String Formatting | Repractiq",
        metaDescription: "Convert structural unix epoch timestamps into readable format fields using the native time module.",
        tags: ["modules"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `format_epoch(seconds)` that imports the native `time` module inside and formats an epoch timestamp into a clean year string `YYYY`.",
        explanation: "Using specialized library namespaces extracts metadata safely without requiring manual time zone calculations.",
        hint: "Use `time.gmtime(seconds)` combined with structural attribute accesses.",
        starterCode: `def format_epoch(seconds):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "1710000000", call: "format_epoch(1710000000)", expected: "'2024'" }
        ],
      },
      {
        id: 81,
        title: "JSON Configuration Extractor Log",
        slug: "python-json-configuration-extractor-log",
        seoTitle: "Python Simulated JSON String Extraction | Repractiq",
        metaDescription: "Parse text blocks to dynamically isolate and read matching configuration records safely.",
        tags: ["file handling", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `parse_simulated_json(config_str)` that splits structural text rows by a colon delimiter, building a clean configuration mapping dictionary out of the parsed entries.",
        explanation: "Custom string line tokenizers extract key-value data fields smoothly, bypassing the need for heavy comprehensive file system drivers.",
        hint: "Use `.split(':', 1)` to handle properties containing value assignments safely.",
        starterCode: `def parse_simulated_json(config_str):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'host:localhost\\nport:8080'", call: "dict(sorted(parse_simulated_json('host:localhost\\\\nport:8080').items()))", expected: "{'host': 'localhost', 'port': '8080'}" }
        ],
      },
      {
        id: 82,
        title: "Nested Index Safeguard Matrix",
        slug: "python-nested-index-safeguard-matrix",
        seoTitle: "Python Safe Matrix Index Fallbacks | Repractiq",
        metaDescription: "Protect two-dimensional array index calls from unexpected IndexError crashes using robust runtime logic.",
        tags: ["error handling", "arrays"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `safe_matrix_access(matrix, r, c)` that catches `IndexError` if the row `r` or column `c` coordinates fall outside bounds, returning None instead of crashing.",
        explanation: "Defending index coordinates with explicit error handlers keeps multi-tiered grid logic running smoothly despite irregular collection sizes.",
        hint: "Wrap the matrix lookup statement `matrix[r][c]` entirely inside a standard try-except block.",
        starterCode: `def safe_matrix_access(matrix, r, c):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 2], [3, 4]], 5, 0", call: "safe_matrix_access([[1, 2], [3, 4]], 5, 0)", expected: "None" }
        ],
      },
      {
        id: 83,
        title: "Employee Roster Inheritance Matrix",
        slug: "python-employee-roster-inheritance-matrix",
        seoTitle: "Python OOP Super Subclass Properties | Repractiq",
        metaDescription: "Extend base class architectures to construct specialized sub-objects using standard inheritance mechanics.",
        tags: ["oop basics"],
        difficulty: "Medium",
        type: "function",
        description: "Design an inheritance flow simulated by `get_manager_details(name, salary, department)` that instantiates a child Manager class expanding a base Employee framework, appending custom attributes. Return its property values.",
        explanation: "The super() keyword passes initialization parameters upward, initializing parent class properties reliably within subclass instances.",
        hint: "Call `super().__init__(name, salary)` inside the child constructor.",
        starterCode: `def get_manager_details(name, salary, department):\n    # Simulate Employee/Manager class relationships\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Alice', 90000, 'HR'", call: "get_manager_details('Alice', 90000, 'HR')", expected: "['Alice', 90000, 'HR']" }
        ],
      },
      {
        id: 84,
        title: "Dynamic String Sorter via Variable Lambda Keys",
        slug: "python-dynamic-string-sorter-lambda",
        seoTitle: "Python Variable Lambda Sorting Keys | Repractiq",
        metaDescription: "Reorder lists of strings dynamically using flexible sorting strategies defined by variable lambdas.",
        tags: ["sorting", "lambda functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `sort_strings_custom(strs, strategy)` that sorts a list of strings by word length if `strategy` is 'length', or sorts them by their final character if `strategy` is 'last_char'.",
        explanation: "Passing dynamic lambda references into standard sorting workflows configures multi-functional algorithms at runtime.",
        hint: "Use the custom sorting condition: `key=lambda x: x[-1]` to sort by the last character of each string.",
        starterCode: `def sort_strings_custom(strs, strategy):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['apple', 'cat', 'banana'], 'length'", call: "sort_strings_custom(['apple', 'cat', 'banana'], 'length')", expected: "['cat', 'apple', 'banana']" }
        ],
      },
      {
        id: 85,
        title: "Extract Odd Numerical Cubes via Map-Filter",
        slug: "python-extract-odd-numerical-cubes",
        seoTitle: "Python Map Filter Cube Compaction | Repractiq",
        metaDescription: "Filter odd integers from a list and calculate their cubes using high-performance functional processing pipelines.",
        tags: ["map/filter"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `cube_odd_numbers(nums)` that isolates all odd numbers from an integer collection and returns a list containing the cubed values of those matching items.",
        explanation: "Chaining filter and map steps creates highly readable, optimized code for vector data processing pipelines.",
        hint: "Use `lambda x: x % 2 != 0` to identify and filter for odd number values.",
        starterCode: `def cube_odd_numbers(nums):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3]", call: "cube_odd_numbers([1, 2, 3])", expected: "[1, 27]" }
        ],
      },
      {
        id: 86,
        title: "Deep Search Dictionary Key Match",
        slug: "python-deep-search-dictionary-key-match",
        seoTitle: "Python Multi Tiered Dictionary Property Lookup | Repractiq",
        metaDescription: "Traverse multi-tiered nested dictionary trees to retrieve properties from specific key paths.",
        tags: ["nested data structures", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `get_nested_value(data, path_keys)` that takes a nested dictionary and a list of string keys, tracking the path down to return the final target value. Return None if any key path breaks.",
        explanation: "Iterating safely down key paths prevents runtime errors when parsing deeply nested JSON metadata fields.",
        hint: "Loop through the list of keys, checking `isinstance(current, dict)` before advancing down the tree.",
        starterCode: `def get_nested_value(data, path_keys):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': {'b': 42}}, ['a', 'b']", call: "get_nested_value({'a': {'b': 42}}, ['a', 'b'])", expected: "42" }
        ],
      },
      {
        id: 87,
        title: "Construct Abbreviated Word Tokens",
        slug: "python-construct-abbreviated-word-tokens",
        seoTitle: "Python Word Acronym String Compaction | Repractiq",
        metaDescription: "Extract and capitalize the initial characters of a phrase to generate concise text acronyms.",
        tags: ["string manipulation"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `generate_acronym(phrase)` that extracts the first letter of every word in a given phrase, converts it to uppercase, and combines them into a single acronym string.",
        explanation: "Tokenizing phrases into component substrings makes it easy to isolate target index properties for code refactoring.",
        hint: "Use `.split()` followed by a list comprehension that extracts the first character (`word[0]`) from each word.",
        starterCode: `def generate_acronym(phrase):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'hyper text markup language'", call: "generate_acronym('hyper text markup language')", expected: "'HTML'" }
        ],
      },
      {
        id: 88,
        title: "Sort Binary Matrix Rows by Population Density",
        slug: "python-sort-binary-matrix-rows-density",
        seoTitle: "Python Binary Array Matrix Sorting | Repractiq",
        metaDescription: "Sort the rows of a binary matrix based on how many active indicator tokens they contain.",
        tags: ["sorting", "arrays"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sort_matrix_by_ones(matrix)` that sorts the horizontal rows of a binary grid in ascending order based on the total number of ones in each row.",
        explanation: "Passing built-in accumulation functions like sum() directly into sorting operations reorders matrix dimensions cleanly.",
        hint: "Configure the sorting behavior using the row calculation key: `key=sum`.",
        starterCode: `def sort_matrix_by_ones(matrix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 1, 0], [1, 0, 0], [1, 1, 1]]", call: "sort_matrix_by_ones([[1, 1, 0], [1, 0, 0], [1, 1, 1]])", expected: "[[1, 0, 0], [1, 1, 0], [1, 1, 1]]" }
        ],
      },
      {
        id: 89,
        title: "Recursive Greatest Common Divisor Engine",
        slug: "python-recursive-gcd-engine",
        seoTitle: "Python Recursive Euclidean GCD Algorithm | Repractiq",
        metaDescription: "Compute the greatest common divisor between two numbers using the recursive Euclidean algorithm.",
        tags: ["recursion basics", "math"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `compute_gcd_recursive(a, b)` that calculates the greatest common divisor of two integers using the classic Euclidean algorithm.",
        explanation: "The Euclidean algorithm simplifies divisor pairs recursively by replacing the larger number with the remainder of their division until the remainder reaches zero.",
        hint: "The base relation reduces down smoothly following the structural formula: `compute_gcd_recursive(b, a % b)`.",
        starterCode: `def compute_gcd_recursive(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "48, 18", call: "compute_gcd_recursive(48, 18)", expected: "6" }
        ],
      },
      {
        id: 90,
        title: "Simulated Custom Range Namespace Generator",
        slug: "python-simulated-custom-range-namespace",
        seoTitle: "Python Random Module Element Shuffling | Repractiq",
        metaDescription: "Generate customized sequences and randomize their order using built-in module distributions.",
        tags: ["modules"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `generate_random_step_list(start, end, step)` that uses Python's core functionality to build a numerical list from `start` up to `end` at the given `step` interval, returning the array.",
        explanation: "Native sequence generation utilities build numeric arrays efficiently, avoiding the overhead of custom tracking loops.",
        hint: "Use the built-in `list(range(start, end, step))` expression to generate the array.",
        starterCode: `def generate_random_step_list(start, end, step):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "0, 10, 2", call: "generate_random_step_list(0, 10, 2)", expected: "[0, 2, 4, 6, 8]" }
        ],
      },
      {
        id: 91,
        title: "Safe Key Lookup with Nested Dictionary Fallback",
        slug: "python-safe-key-lookup-dictionary-fallback",
        seoTitle: "Python Safe Dictionary Key Retrieval | Repractiq",
        metaDescription: "Read values from structured dictionary configurations safely using KeyError protection mechanisms.",
        tags: ["error handling", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `safe_key_lookup(d, key, fallback)` that queries a dictionary configuration for a specific target key parameter. If a `KeyError` is thrown, catch the exception cleanly and return the `fallback` string.",
        explanation: "Intercepting KeyError exceptions prevents processing crashes when runtime environments supply dynamic or missing mapping schemas.",
        hint: "Implement a direct try-except block wrapping the standard index query: `d[key]`.",
        starterCode: `def safe_key_lookup(d, key, fallback):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': 1}, 'b', 'Missing'", call: "safe_key_lookup({'a': 1}, 'b', 'Missing')", expected: "'Missing'" }
        ],
      },
      {
        id: 92,
        title: "Simulated Persistent System File Append",
        slug: "python-simulated-persistent-file-append",
        seoTitle: "Python Context Stream Log Appending | Repractiq",
        metaDescription: "Simulate modifying textual content records safely without wiping pre-existing row structures.",
        tags: ["file handling"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `append_log_entry(current_content, new_entry)` that takes the text of a file and appends a new entry on a new line, returning the updated full text content.",
        explanation: "Simulating append workflows ensures records are tracked continuously across active software layers.",
        hint: "Combine the strings with a newline character `\\n` to cleanly format the combined log payload.",
        starterCode: `def append_log_entry(current_content, new_entry):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Log v1', 'Error 500'", call: "append_log_entry('Log v1', 'Error 500')", expected: "'Log v1\\nError 500'" }
        ],
      },
      {
        id: 93,
        title: "Protected Product Ledger with Safe Mutators",
        slug: "python-protected-product-ledger-mutators",
        seoTitle: "Python OOP Enforced Attribute Encapsulation | Repractiq",
        metaDescription: "Enforce class properties boundaries securely to control internal application state modifications.",
        tags: ["oop basics"],
        difficulty: "Medium",
        type: "function",
        description: "Implement a tracking setup simulated by `update_product_price(initial_price, adjustment)` that creates an internal class with a private `__price` attribute. Prevent updates that reduce the price below zero, and return the final price.",
        explanation: "Private prefixes protect inner variables from invalid external adjustments, channeling updates through controlled class methods.",
        hint: "Raise an error or ignore changes inside the class method if the new target price evaluates to a negative number.",
        starterCode: `def update_product_price(initial_price, adjustment):\n    # Simulate internal encapsulated property modification logic\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "100, -20", call: "update_product_price(100, -20)", expected: "80" },
          { input: "50, -60", call: "update_product_price(50, -60)", expected: "50" }
        ],
      },
      {
        id: 94,
        title: "Conditional Power Exponential Creator via Lambdas",
        slug: "python-conditional-power-exponential-lambdas",
        seoTitle: "Python High-Order Functional Power Closures | Repractiq",
        metaDescription: "Generate inline numeric equations dynamically using configurable anonymous functional wrappers.",
        tags: ["lambda functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `power_generator(exponent)` that builds and returns a functional lambda definition. The resulting inner closure should take a variable parameter `base` and raise it to the predetermined `exponent`.",
        explanation: "Dynamic functional programming constructs mathematical functions on the fly by storing baseline scaling rules inside closure memory maps.",
        hint: "Construct the expression as an anonymous lambda statement: `lambda base: base ** exponent`.",
        starterCode: `def power_generator(exponent):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "3", call: "power_generator(3)(2)", expected: "8" }
        ],
      },
      {
        id: 95,
        title: "Filter Unique Domains via Functional Chains",
        slug: "python-filter-unique-domains-functional",
        seoTitle: "Python Sequence Normalization Mapping | Repractiq",
        metaDescription: "Clean dynamic list inputs quickly using integrated functional filter and map utility layers.",
        tags: ["map/filter", "lambda functions"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `clean_domains(domains)` that filters out entries shorter than 5 characters and uses `map` to convert the remaining valid strings completely to lowercase, returning a list.",
        explanation: "Chaining structural map and filter routines handles data cleanup smoothly without the boilerplate of tracking indexes.",
        hint: "Wrap the lazy return collection within a clear structural `list()` container before exporting.",
        starterCode: `def clean_domains(domains):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['GOOGLE.COM', 'fb', 'MSN.COM']", call: "clean_domains(['GOOGLE.COM', 'fb', 'MSN.COM'])", expected: "['google.com', 'msn.com']" }
        ],
      },
      {
        id: 96,
        title: "Aggregate Multilevel Corporate Inventory",
        slug: "python-aggregate-multilevel-corporate-inventory",
        seoTitle: "Python Multi-Tiered Dictionary Aggregation | Repractiq",
        metaDescription: "Extract hidden structural variables nested inside multi-layered enterprise department matrices.",
        tags: ["nested data structures"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sum_department_assets(company_data)` that iterates through a nested dictionary where keys are departments and values are lists of items with a 'cost' attribute, calculating total company assets cost.",
        explanation: "Looping through deeply nested dictionary structures lets you aggregate values distributed across complex object layers.",
        hint: "Use nested loops to access the inner lists of dictionaries, accumulating the 'cost' value from each item.",
        starterCode: `def sum_department_assets(company_data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'HR': [{'cost': 10}], 'IT': [{'cost': 50}, {'cost': 40}]}", call: "sum_department_assets({'HR': [{'cost': 10}], 'IT': [{'cost': 50}, {'cost': 40}]})", expected: "100" }
        ],
      },
      {
        id: 97,
        title: "Clean Irregular Phone Logs via Index Extraction",
        slug: "python-clean-irregular-phone-logs",
        seoTitle: "Python String Content Formatting Sanitation | Repractiq",
        metaDescription: "Format numerical records cleanly by stripping punctuation artifacts and irregular space pads.",
        tags: ["string manipulation"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `format_phone_number(raw_str)` that removes spaces, hyphens, and brackets from a string, returning a clean numerical sequence string.",
        explanation: "Chaining `.replace()` statements converts messy input fields into standardized values for database updates.",
        hint: "Use a simple loop or a chain of string replace functions like `.replace('-', '')` to remove unwanted characters.",
        starterCode: `def format_phone_number(raw_str):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'(123) 456-7890'", call: "format_phone_number('(123) 456-7890')", expected: "'1234567890'" }
        ],
      },
      {
        id: 98,
        title: "Sort Hybrid Asset Metrics by Dynamic Priority",
        slug: "python-sort-hybrid-asset-metrics",
        seoTitle: "Python Multi Attribute Lambda Ranking | Repractiq",
        metaDescription: "Sort multi-property object records by their computed values using custom sorting metrics.",
        tags: ["sorting", "lambda functions"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sort_assets(assets)` that reorders a list of dictionaries in descending order based on a custom efficiency score calculated as `value / weight`.",
        explanation: "Custom sorting keys let you organize complex models dynamically by prioritizing computed metrics over raw property values.",
        hint: "Pass a custom key function inside sorted: `key=lambda x: x['value'] / x['weight']`, and set `reverse=True`.",
        starterCode: `def sort_assets(assets):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'value': 10, 'weight': 2}, {'value': 20, 'weight': 2}]", call: "sort_assets([{'value': 10, 'weight': 2}, {'value': 20, 'weight': 2}])", expected: "[{'value': 20, 'weight': 2}, {'value': 10, 'weight': 2}]" }
        ],
      },
      {
        id: 99,
        title: "Count String Character Matches via Recursion",
        slug: "python-count-string-character-matches-recursion",
        seoTitle: "Python Recursive Character Occurrence Search | Repractiq",
        metaDescription: "Count character occurrences within dynamic string blocks using a recursive lookup loop.",
        tags: ["recursion basics", "strings"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `count_char_recursive(s, char)` that determines how many times a target letter appears inside a string using recursive slicing.",
        explanation: "Recursive string functions inspect characters one slice at a time, avoiding loop variables by shifting the rest of the work to subsequent calls.",
        hint: "Check if the first letter matches the target, add 1 if it does, and pass `s[1:]` to the next step.",
        starterCode: `def count_char_recursive(s, char):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'apple', 'p'", call: "count_char_recursive('apple', 'p')", expected: "2" },
          { input: "'abc', 'z'", call: "count_char_recursive('abc', 'z')", expected: "0" }
        ],
      },
      {
        id: 100,
        title: "Simulated Math Combinations Calculation Engine",
        slug: "python-simulated-math-combinations-engine",
        seoTitle: "Python Namespace Library Math Utilities | Repractiq",
        metaDescription: "Compute combination counts safely using optimal mathematical formulas from the math library.",
        tags: ["modules", "math"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `compute_combinations(n, k)` that imports the native `math` library internally to find the total unique ways to select `k` elements out of a group of size `n`.",
        explanation: "Using standard mathematical methods provides optimized, highly accurate computations right out of the box.",
        hint: "Call `math.comb(n, k)` directly from the imported math library module context.",
        starterCode: `def compute_combinations(n, k):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "5, 2", call: "compute_combinations(5, 2)", expected: "10" }
        ],
      },
      {
        id: 101,
        title: "Extract Configuration Profiles from Inline Rows",
        slug: "python-extract-configuration-profiles-rows",
        seoTitle: "Python Custom Token Row Splitting | Repractiq",
        metaDescription: "Parse structured multi-line text blocks into key-value data maps using string segmentation tools.",
        tags: ["file handling", "string manipulation"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `parse_key_value_file(file_content)` that processes a multi-line string containing `key=value` lines, building and returning a clean lookup dictionary.",
        explanation: "Custom parsers split incoming data by delimiter keywords, allowing you to easily extract key configuration details.",
        hint: "Iterate over the lines using `.splitlines()`, then split each valid line on the `=` character.",
        starterCode: `def parse_key_value_file(file_content):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'env=prod\\ndebug=false'", call: "dict(sorted(parse_key_value_file('env=prod\\\\ndebug=false').items()))", expected: "{'debug': 'false', 'env': 'prod'}" }
        ],
      },
      {
        id: 102,
        title: "Safe Numeric Divisor with Value Check Fallback",
        slug: "python-safe-numeric-divisor-fallback",
        seoTitle: "Python Catch Division by Zero Runtime Exception | Repractiq",
        metaDescription: "Protect critical division logic against ZeroDivisionError exceptions using robust try-catch blocks.",
        tags: ["error handling"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `safe_divide_values(numerator, denominator)` that catches `ZeroDivisionError` if the denominator equals zero, returning a fallback float value of 0.0.",
        explanation: "Catching zero division errors handles calculation anomalies gracefully, keeping the wider math engine from crashing.",
        hint: "Wrap the division operation inside a standard try block and catch `ZeroDivisionError`.",
        starterCode: `def safe_divide_values(numerator, denominator):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "10, 0", call: "safe_divide_values(10, 0)", expected: "0.0" },
          { input: "10, 2", call: "safe_divide_values(10, 2)", expected: "5.0" }
        ],
      },
      {
        id: 103,
        title: "Polymorphic Shape Area Calculator",
        slug: "python-polymorphic-shape-area-calculator",
        seoTitle: "Python OOP Polymorphism Interface Design | Repractiq",
        metaDescription: "Build out clean geometric class variations that compute distinct object areas using method overriding.",
        tags: ["oop basics"],
        difficulty: "Medium",
        type: "function",
        description: "Create a polymorphic method structure simulated by `calculate_shape_area(shape_type, dimension)` where distinct classes (`Square`, `Circle`) inherit from a common base and override an `area()` method.",
        explanation: "Polymorphism simplifies interface usage by letting different subclasses implement custom versions of the same core method name.",
        hint: "Use `math.pi` or `3.14159` to accurately compute the circle calculation area.",
        starterCode: `def calculate_shape_area(shape_type, dimension):\n    # Simulate Square and Circle child configurations dynamically\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Square', 4", call: "calculate_shape_area('Square', 4)", expected: "16" },
          { input: "'Circle', 10", call: "round(calculate_shape_area('Circle', 10), 1)", expected: "314.2" }
        ],
      },
      {
        id: 104,
        title: "Variable Range Multiplier via Higher-Order Lambdas",
        slug: "python-variable-range-multiplier-lambdas",
        seoTitle: "Python Higher-Order Scope Lambda Closures | Repractiq",
        metaDescription: "Generate inline multiplication functions dynamically using nested lambda expressions.",
        tags: ["lambda functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `create_adder(n)` that returns an anonymous function. The returned function accepts a single parameter `x` and adds it to the pre-configured value `n`.",
        explanation: "Functional interfaces capture outer parameters, building flexible math formulas that can be reused anywhere in your code.",
        hint: "Return a standard lambda expression configured as: `lambda x: x + n`.",
        starterCode: `def create_adder(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "10", call: "create_adder(10)(5)", expected: "15" }
        ],
      },
      {
        id: 105,
        title: "Extract Non-Empty Strings via Functional Engine",
        slug: "python-extract-non-empty-strings-functional",
        seoTitle: "Python Map Filter Blank String Cleanup | Repractiq",
        metaDescription: "Clean messy array inputs by filtering out blank values and stripping whitespace using functional utility steps.",
        tags: ["map/filter"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `clean_string_list(items)` that filters out empty strings or strings that only contain whitespace, and uses `map` to remove all leading and trailing spaces from the remaining words.",
        explanation: "Chaining filter and map logic keeps string arrays clean without cluttering your code with complex nested loops.",
        hint: "Filter list elements by checking `.strip() != ''` to catch empty strings, then apply `.strip()` with a map.",
        starterCode: `def clean_string_list(items):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['  hello ', '', ' world  ', '   ']", call: "clean_string_list(['  hello ', '', ' world  ', '   '])", expected: "['hello', 'world']" }
        ],
      },
      {
        id: 106,
        title: "Flatten Three-Dimensional Numeric Matrix Maps",
        slug: "python-flatten-three-dimensional-matrix-maps",
        seoTitle: "Python Unpack Multi Tiered Deep Matrix | Repractiq",
        metaDescription: "Flatten nested deep numerical matrices into single flat arrays for analytics processing loops.",
        tags: ["nested data structures", "arrays"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `flatten_3d_matrix(matrix)` that accepts a three-dimensional list structure (a list of lists of lists) and flattens all internal integers into a single, flat list.",
        explanation: "Unpacking multi-layered nested sequences consolidates multi-dimensional matrix values into simple collections for easier tracking.",
        hint: "Use three levels of nested loops or integrated list comprehensions to unpack items at the deepest level.",
        starterCode: `def flatten_3d_matrix(matrix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[[1, 2]], [[3, 4]]]", call: "flatten_3d_matrix([[[1, 2]], [[3, 4]]])", expected: "[1, 2, 3, 4]" }
        ],
      },
      {
        id: 107,
        title: "Extract Target Words via Delimiter Tokenizer",
        slug: "python-extract-target-words-tokenizer",
        seoTitle: "Python Custom Symbol String Parsing | Repractiq",
        metaDescription: "Parse complex string blocks using custom punctuation split routines to extract word elements cleanly.",
        tags: ["string manipulation"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `split_by_multiple_delimiters(text)` that replaces slashes `/` and vertical pipes `|` with standard spaces, splits the text, and returns a clean list of individual words.",
        explanation: "Replacing irregular divider symbols with spaces normalizes string formats for quick tokenization.",
        hint: "Use `.replace('/', ' ').replace('|', ' ')` before calling the standard split method.",
        starterCode: `def split_by_multiple_delimiters(text):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'unix/bin|share'", call: "split_by_multiple_delimiters('unix/bin|share')", expected: "['unix', 'bin', 'share']" }
        ],
      },
      {
        id: 108,
        title: "Sort Names by Surname Alphabetical Rank",
        slug: "python-sort-names-surname-alphabetical",
        seoTitle: "Python Custom Token Lambda Reordering | Repractiq",
        metaDescription: "Sort full name lists alphabetically based on the last name field using a custom lambda function.",
        tags: ["sorting", "string manipulation"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sort_by_surname(names)` that takes a list of full names (First Last) and sorts them alphabetically by their last name.",
        explanation: "Splitting strings inside a sorting key function lets you organize lists based on internal substrings instead of the initial character.",
        hint: "Extract the last name for sorting by using the expression: `key=lambda x: x.split()[-1]`.",
        starterCode: `def sort_by_surname(names):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['John Smith', 'Alice Brown']", call: "sort_by_surname(['John Smith', 'Alice Brown'])", expected: "['Alice Brown', 'John Smith']" }
        ],
      },
      {
        id: 109,
        title: "Recursive Array Element Maximizer",
        slug: "python-recursive-array-element-maximizer",
        seoTitle: "Python Recursive List Maximum Element Search | Repractiq",
        metaDescription: "Find the maximum integer inside a list using recursive comparisons instead of the built-in max() function.",
        tags: ["recursion basics", "arrays"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `find_max_recursive(lst)` that identifies the largest integer inside an array using recursive breakdown operations, without relying on the built-in `max()` function.",
        explanation: "Recursive search functions compare the first element against the maximum value found in the remaining list slice, bubbling up the largest value.",
        hint: "The base case returns the single remaining element when the list size drops down to one.",
        starterCode: `def find_max_recursive(lst):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 5, 3, 9, 2]", call: "find_max_recursive([1, 5, 3, 9, 2])", expected: "9" },
          { input: "[7]", call: "find_max_recursive([7])", expected: "7" }
        ],
      },
      {
        id: 110,
        title: "Dynamic Date Calculator via Native Module Scope",
        slug: "python-dynamic-date-calculator-native-module",
        seoTitle: "Python Datetime Delta Calculation Offset | Repractiq",
        metaDescription: "Calculate past dates dynamically using core datetime modules and duration tracking objects.",
        tags: ["modules"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `calculate_past_date(days_back)` that imports the native `datetime` module inside and returns a string formatted as `YYYY-MM-DD` representing the date exactly `days_back` from a baseline epoch start date (2026-07-14).",
        explanation: "Standard tracking libraries compute date modifications cleanly by handling complex adjustments like leap years automatically.",
        hint: "Create a reference point using `datetime.date(2026, 7, 14)` and subtract a configured `datetime.timedelta(days=days_back)`.",
        starterCode: `def calculate_past_date(days_back):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "10", call: "calculate_past_date(10)", expected: "'2026-07-04'" }
        ],
      },
      {
        id: 111,
        title: "Verify Matrix Boundaries via Boundary Check Loop",
        slug: "python-verify-matrix-boundaries-loop",
        seoTitle: "Python Coordinate Validation Security | Repractiq",
        metaDescription: "Validate dimensional matrix lookups using boundary verification steps to prevent index runtime crashes.",
        tags: ["error handling", "arrays"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `is_valid_coordinate(matrix, r, c)` that confirms whether the coordinates row `r` and column `c` fall safely within the current matrix boundaries, returning True if valid and False otherwise.",
        explanation: "Validating coordinate indexes before looking up elements keeps applications from crashing due to unexpected out-of-bounds inputs.",
        hint: "Check that `0 <= r < len(matrix)` and `0 <= c < len(matrix[0])` evaluate to true.",
        starterCode: `def is_valid_coordinate(matrix, r, c):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 2], [3, 4]], 1, 1", call: "is_valid_coordinate([[1, 2], [3, 4]], 1, 1)", expected: "True" },
          { input: "[[1, 2]], 2, 0", call: "is_valid_coordinate([[1, 2]], 2, 0)", expected: "False" }
        ],
      },
      {
        id: 112,
        title: "Generate Key Mappings from Text Blocks",
        slug: "python-generate-key-mappings-text-blocks",
        seoTitle: "Python Multi String Row Conversion Mapping | Repractiq",
        metaDescription: "Transform unstructured multi-line text strings into clean dictionary objects for log analysis workloads.",
        tags: ["file handling", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `build_word_index_file(text_content)` that takes a line of space-separated words, counting how many times each word appears and returning the final count mapping dictionary.",
        explanation: "Parsing text files using basic token split logic helps extract text parameters and count items efficiently.",
        hint: "Use `.split()` to extract words, then keep track of frequencies using a standard dictionary.",
        starterCode: `def build_word_index_file(text_content):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'apple banana apple'", call: "dict(sorted(build_word_index_file('apple banana apple').items()))", expected: "{'banana': 1, 'apple': 2}" }
        ],
      },
      {
        id: 113,
        title: "Corporate Account Inheritance Wrapper",
        slug: "python-corporate-account-inheritance",
        seoTitle: "Python OOP Subclass Override Properties | Repractiq",
        metaDescription: "Extend simple base class blueprints to add specialized accounting rules using object inheritance patterns.",
        tags: ["oop basics"],
        difficulty: "Medium",
        type: "function",
        description: "Design an inheritance structure simulated by `get_savings_interest(balance, rate)` where a child SavingsAccount class extends a standard base Account setup, incorporating an additional interest calculation parameter.",
        explanation: "Inheritance lets you build specialized child objects that reuse core parent methods while implementing custom business requirements.",
        hint: "Pass the initial balance parameters up to the parent constructor using `super().__init__(balance)`.",
        starterCode: `def get_savings_interest(balance, rate):\n    # Simulate Account and SavingsAccount processing hierarchies\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "1000, 0.05", call: "get_savings_interest(1000, 0.05)", expected: "50.0" }
        ],
      },
      {
        id: 114,
        title: "Custom String Concatenator via Flexible Lambdas",
        slug: "python-custom-string-concatenator-lambdas",
        seoTitle: "Python Functional Prefix Lambda Wrapper | Repractiq",
        metaDescription: "Generate inline prefix strings dynamically using customizable anonymous lambda assignments.",
        tags: ["lambda functions", "strings"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `make_prefix_logger(prefix)` that creates and returns a lambda function. The lambda should take an argument `message` and prepend the pre-configured prefix string (e.g., '[prefix] message').",
        explanation: "Using closures to wrap lambda components makes it easy to generate reusable string prefix handlers on the fly.",
        hint: "Return an anonymous function formatted as: `lambda message: f'{prefix} {message}'`.",
        starterCode: `def make_prefix_logger(prefix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'[INFO]'", call: "make_prefix_logger('[INFO]')('System OK')", expected: "'[INFO] System OK'" }
        ],
      },
      {
        id: 115,
        title: "Filter High Asset Values via Map-Filter Matrix",
        slug: "python-filter-high-asset-values",
        seoTitle: "Python Functional List Transformation Pipeline | Repractiq",
        metaDescription: "Filter dynamic arrays and scale down their internal parameters using functional map and filter chains.",
        tags: ["map/filter"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `discount_expensive_items(prices, limit)` that filters out any cost values below the `limit` threshold, and applies a 10% discount to all remaining high-value items using `map`.",
        explanation: "Chaining map and filter steps processing routines allows you to cleanly filter data and transform entries in a single pipeline.",
        hint: "Filter for values greater than or equal to the limit first: `lambda p: p >= limit`.",
        starterCode: `def discount_expensive_items(prices, limit):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[50, 100, 200], 100", call: "discount_expensive_items([50, 100, 200], 100)", expected: "[90.0, 180.0]" }
        ],
      },
      {
        id: 116,
        title: "Query Nested Config Matrix Values Safely",
        slug: "python-query-nested-config-matrix",
        seoTitle: "Python Safe Deep Matrix Key Resolution | Repractiq",
        metaDescription: "Extract metadata values from nested configurations safely by protecting lookups against missing keys.",
        tags: ["nested data structures", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `fetch_db_port(config_data)` that attempts to safely read the value located at `config_data['settings']['db']['port']`. Return -1 if any intermediate key does not exist.",
        explanation: "Adding conditional key checks at each stage prevents runtime failures when parsing complex nested parameters.",
        hint: "Use safe lookup chains or verify key inclusion sequentially using statements like `if 'settings' in config_data:`.",
        starterCode: `def fetch_db_port(config_data):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'settings': {'db': {'port': 5432}}}", call: "fetch_db_port({'settings': {'db': {'port': 5432}}})", expected: "5432" },
          { input: "{'settings': {}}", call: "fetch_db_port({'settings': {}})", expected: "-1" }
        ],
      },
      {
        id: 117,
        title: "Compress Duplicate Characters via Reduction Loops",
        slug: "python-compress-duplicate-characters",
        seoTitle: "Python Character Run Length Compaction | Repractiq",
        metaDescription: "Compress repeating character sequences into compact token strings using character tracking loops.",
        tags: ["string manipulation"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `collapse_spaces(s)` that identifies consecutive space sequences within an input text block and collapses them into a single space character.",
        explanation: "Cleaning up duplicate space buffers transforms unstructured text records into cleanly formatted fields.",
        hint: "Splitting a string by default `.split()` parameters and re-joining it with a single space automatically eliminates extra whitespace.",
        starterCode: `def collapse_spaces(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'hello   world  again'", call: "collapse_spaces('hello   world  again')", expected: "'hello world again'" }
        ],
      },
      {
        id: 118,
        title: "Sort Numeric Matrix Elements by Frequency Density",
        slug: "python-sort-matrix-frequency-density",
        seoTitle: "Python Value Counting Sort Reordering | Repractiq",
        metaDescription: "Sort flat collection items based on how frequently each element appears across the data list.",
        tags: ["sorting", "dictionaries"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `sort_by_frequency(nums)` that reorders an array so that items that appear more frequently come first. Resolve frequency ties by sorting values in ascending numerical order.",
        explanation: "Using custom tuple elements within sorting key functions makes it easy to handle complex sorting workflows with multiple fallback criteria.",
        hint: "Set the sorting key to return a multi-part tuple: `key=lambda x: (-nums.count(x), x)`.",
        starterCode: `def sort_by_frequency(nums):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[4, 4, 2, 8, 2, 2]", call: "sort_by_frequency([4, 4, 2, 8, 2, 2])", expected: "[2, 2, 2, 4, 4, 8]" }
        ],
      },
      {
        id: 119,
        title: "Recursive Count of Multi-Nested List Elements",
        slug: "python-recursive-count-multi-nested-list",
        seoTitle: "Python Recursive Element Unpacking Count | Repractiq",
        metaDescription: "Count the total number of individual integers inside deeply nested lists using recursive checking loops.",
        tags: ["recursion basics", "arrays"],
        difficulty: "Medium",
        type: "function",
        description: "Write a function `deep_count_elements(lst)` that calculates the total count of individual integers stored across an array that contains multi-nested lists.",
        explanation: "Recursive list validation handles deeply nested structures smoothly by inspecting sub-arrays step-by-step and summing up the total item count.",
        hint: "Loop through items: if an element `isinstance(x, list)` is true, recursively call the counting function; otherwise, add 1.",
        starterCode: `def deep_count_elements(lst):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, [2, 3], [[4]]]", call: "deep_count_elements([1, [2, 3], [[4]]])", expected: "4" }
        ],
      },
      {
        id: 120,
        title: "Simulated System Math Value Flooring Matrix",
        slug: "python-simulated-system-math-flooring",
        seoTitle: "Python Optimization Math Ceiling Floors | Repractiq",
        metaDescription: "Round float values down to nearest integer milestones using optimized math functions.",
        tags: ["modules", "math"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function `floor_float_value(val)` that imports Python's native `math` module internally and uses it to calculate the mathematical floor of a floating-point number.",
        explanation: "Importing modules locally keeps scope clean and ensures your code runs optimized mathematical operations.",
        hint: "Call `math.floor(val)` directly to return the nearest lower integer milestone.",
        starterCode: `def floor_float_value(val):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "5.7", call: "floor_float_value(5.7)", expected: "5" }
        ],
      },
  ];